import React, { useState, useMemo, useRef, useCallback, useEffect, useTransition, useDeferredValue, useContext, createContext } from 'react';
import { Select, Tag, Switch, Tooltip, Popover } from 'antd';
import { CloseOutlined, HolderOutlined, FilterOutlined, TableOutlined, SettingOutlined } from '@ant-design/icons';
import { ArrowUpWideNarrow, ArrowDownWideNarrow, HistoryIcon, ChevronDown, Check } from 'lucide-react';
import GenericCustomButton from '../../../genericComponents/GenericCustomButton';
import { dispatchHandler, handleApiChange } from '../../../helper/apiHelperFunctions';
import { useDispatch, useSelector } from 'react-redux';
import { getPermissionKey } from '../../../helper/permissions';
import { CONFIG } from '../../../config';
import NoDataFound from '../../../genericComponents/NoDataFound';
import GenericInput from '../../../genericComponents/input/GenericInputComponent';
import { ReloadContext } from '../../../App';
import GenericToggleComponent from '../../../genericComponents/GenericToggleComponent';
import useCustomToast from '../../../hooks/useCustomToast';
import ParentModal from '../../../genericComponents/ParentModal';
import ViewCountData from './ViewCountData';
import { getDataStorageKey } from '../../../helper/helperTableFunctions';
import { openDB } from 'idb';
import Icons from '../../../assets/icons';
import TextWithTooltip from '../../../genericComponents/TextWithTooltip';
import DesignStagesModal from './DesignStagesModal';
import dayjs from 'dayjs';
import LanguageTranslator from '../../../locals/LanguageTranslator';

// ─── CONSTANTS ──────────────────────────────────────────────────────────────────

const AGGREGATIONS = {
    sum: { label: 'Sum', fn: (rows, f) => rows.reduce((a, r) => a + (Number(r[f]) || 0), 0) },
    count: { label: 'Count', fn: (rows) => rows.length },
    avg: { label: 'Average', fn: (rows, f) => { const n = rows.map(r => Number(r[f])).filter(v => !isNaN(v) && v !== null); return n.length ? n.reduce((a, b) => a + b, 0) / n.length : 0; } },
    min: { label: 'Min', fn: (rows, f) => { const n = rows.map(r => Number(r[f])).filter(v => !isNaN(v) && v !== null); return n.length ? Math.min(...n) : 0; } },
    max: { label: 'Max', fn: (rows, f) => { const n = rows.map(r => Number(r[f])).filter(v => !isNaN(v) && v !== null); return n.length ? Math.max(...n) : 0; } },
};

const MONTH_ORDER = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Measure fields. These can ONLY be dropped in the Values area, and the Values
// area accepts ONLY these — keeping dimensions (rows/cols/filters) and measures
// strictly separated so users can't, e.g., group by po_amount or sum a status.
const VALUES_ONLY_FIELDS = ['po_quantity', 'po_count', 'po_amount'];

// On-track ratio is binned into five tiers; each tier maps to a cell-tint and
// a link-style. Thresholds are half-open on the right: e.g. 0.50 falls into
// 'light-red', not 'red'.
function ratioTier(ratio) {
    if (ratio < 0.30) return 'dark-red';
    if (ratio < 0.50) return 'red';
    if (ratio < 0.70) return 'light-red';
    if (ratio < 0.90) return 'light-green';
    return 'dark-green';
}
const RATIO_TINT = {
    'dark-red':    'bg-red-200 text-red-900',
    'red':         'bg-red-100 text-red-800',
    'light-red':   'bg-red-50 text-red-600',
    'light-green': 'bg-emerald-50 text-emerald-700',
    'dark-green':  'bg-emerald-100 text-emerald-900',
};
const RATIO_LINK = {
    'dark-red':    'inline-block text-red-900 font-semibold underline underline-offset-2 decoration-red-400 hover:decoration-red-900 hover:text-red-950',
    'red':         'inline-block text-red-800 font-semibold underline underline-offset-2 decoration-red-300 hover:decoration-red-800 hover:text-red-900',
    'light-red':   'inline-block text-red-600 font-semibold underline underline-offset-2 decoration-red-300 hover:decoration-red-600 hover:text-red-700',
    'light-green': 'inline-block text-emerald-700 font-semibold underline underline-offset-2 decoration-emerald-300 hover:decoration-emerald-700 hover:text-emerald-800',
    'dark-green':  'inline-block text-emerald-900 font-semibold underline underline-offset-2 decoration-emerald-400 hover:decoration-emerald-900 hover:text-emerald-950',
};

// Carries the cell click handler from <TNAPivotTable> down to PivotRow without
// prop-drilling through the memoised hoisted PivotGrid/PivotRow components.
const PivotCellContext = createContext({ onCountClick: null });
const isValuesOnlyField = (field) => VALUES_ONLY_FIELDS.includes(field);

const EMPTY_CONFIG = {
    rowFields: [],
    colFields: [],
    valueFields: [],
    filterFields: [],
    filters: {},
    showSubtotals: true,
    showGrandTotal: true,
};

// ── Default pivot view ────────────────────────────────────────────────────────
// Picked on first mount once headers arrive, so the user lands on a populated
// pivot instead of an empty canvas. The FIRST key in each list that exists in
// `headersData[headersType]` wins. If none match, a name-pattern fallback runs
// (regex below) — so this still works even if the backend renames columns.
const DEFAULT_PREFERENCES = {
    // Try TNA-stage field names in order — backends differ on the exact key.
    rows: ['status_name', 'tna_stage', 'statusName', 'tnaStage'],
    cols: ['cluster', 'sl_cluster'],
    values: [
        { field: 'po_quantity', aggFunc: 'sum' },
        { field: 'po_count', aggFunc: 'sum' },
        { field: 'po_amount', aggFunc: 'sum' },
    ],
};

// Field keys that hold the TNA stage label. Used to decide where to inject
// the tnaStageData order so all configured stages render even with no data.
const TNA_STAGE_FIELD_CANDIDATES = ['status_name', 'tna_stage', 'statusName', 'tnaStage'];

// Pattern-based fallback when no DEFAULT_PREFERENCES key matches. Patterns are
// tried in order; the first one that finds a field wins. This lets TNA-stage
// beat generic month/category fields when picking the default row.
const FALLBACK_ROW_PATTERNS = [
    /tna|stage/i,
    /^status|status_name|status name/i,
    /bucket|category/i,
    /month/i,
    /name$/i,
];
const FALLBACK_COL_PATTERNS = [
    /^cluster$|sl_cluster/i,
    /city|division|dept|site|region|zone/i,
];
const FALLBACK_VAL_PATTERN = /quantity|qty|amount|val|count|total|price|sales/i;

// ─── UTILITIES ───────────────────────────────────────────────────────────────────

function getVal(row, field) {
    const v = row[field];
    return v === null || v === undefined ? '(blank)' : String(v);
}

function makeKey(row, fields) {
    return fields.map(f => getVal(row, f)).join('\x00');
}

function sortCombos(combos, fieldsForCombo = [], customOrders = {}) {
    const valueOrder = {};
    for (const key in customOrders) Object.assign(valueOrder, customOrders[key]);
    const hasCustom = Object.keys(valueOrder).length > 0;

    return [...combos].sort((a, b) => {
        for (let i = 0; i < Math.max(a.length, b.length); i++) {
            const av = a[i] ?? '', bv = b[i] ?? '';
            if (hasCustom) {
                const ai = valueOrder[av];
                const bi = valueOrder[bv];
                const aHas = ai !== undefined;
                const bHas = bi !== undefined;
                if (aHas && bHas) {
                    if (ai !== bi) return ai - bi;
                    continue;
                }
                if (aHas) return -1;
                if (bHas) return 1;
            }
            const am = av.match(/^([A-Za-z]{3})-(\d{4})$/);
            const bm = bv.match(/^([A-Za-z]{3})-(\d{4})$/);
            if (am && bm) {
                const yd = Number(am[2]) - Number(bm[2]);
                if (yd !== 0) return yd;
                const md = MONTH_ORDER.indexOf(am[1]) - MONTH_ORDER.indexOf(bm[1]);
                if (md !== 0) return md;
                // Equal months — fall through to compare the next level (e.g.
                // TNA Stage within the same month).
                continue;
            }
            if (av < bv) return -1;
            if (av > bv) return 1;
        }
        return 0;
    });
}

function aggregate(rows, field, aggFunc) {
    if (!rows || rows.length === 0) return null;
    return AGGREGATIONS[aggFunc].fn(rows, field);
}

// Threshold (absolute) at which values get abbreviated to K/L/Cr. Below this,
// the value is shown in full and gets no hover tooltip (nothing is hidden).
const SHORT_NOTATION_THRESHOLD = 1000;

// Short Indian-notation formatter (matches the Order Tracking dashboard):
//   ≥ 1 Cr  → "x.xx Cr"
//   ≥ 1 L   → "x.xx L"
//   ≥ 1 K   → "x.x K"
//   else    → rounded whole number with Indian grouping (e.g. 120)
// Sign is preserved for negatives. Values are rounded off (no long decimals).
function formatShortIndian(val) {
    const sign = val < 0 ? '-' : '';
    const abs = Math.abs(val);
    if (abs >= 10000000) return `${sign}${(abs / 10000000).toFixed(2)} Cr`;
    if (abs >= 100000) return `${sign}${(abs / 100000).toFixed(2)} L`;
    if (abs >= SHORT_NOTATION_THRESHOLD) return `${sign}${(abs / 1000).toFixed(1)} K`;
    return Math.round(val).toLocaleString('en-IN');
}

// True only when formatShortIndian abbreviates the value (K/L/Cr) — i.e. when
// the displayed text differs from the exact figure and a tooltip adds value.
function isShortened(val) {
    return typeof val === 'number' && !isNaN(val) && Math.abs(val) >= SHORT_NOTATION_THRESHOLD;
}

// Exact Indian-formatted value, shown in the hover tooltip for quantity/amount
// cells so the precise figure is still available behind the short notation.
function formatFullIndian(val) {
    if (val === null || val === undefined || (typeof val === 'number' && isNaN(val))) return '–';
    return Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatNum(val, aggFunc, field) {
    if (val === null || val === undefined || (typeof val === 'number' && isNaN(val))) return '–';
    if (aggFunc === 'count') return val.toLocaleString('en-IN');
    if (typeof val === 'number') {
        // PO Count stays a plain integer (it's a clickable drill-down, not a magnitude).
        if (field === 'po_count') {
            return String(Math.trunc(val));
        }
        // PO Quantity & PO Amount use short Indian notation (L / Cr), rounded off.
        if (field === 'po_quantity' || field === 'po_amount') {
            return formatShortIndian(val);
        }
        if (Math.abs(val) >= 1000 || !Number.isInteger(val)) {
            return val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        }
        return val.toLocaleString('en-IN');
    }
    return String(val);
}

// Detect which keys hold numeric values by sampling rows
function detectNumericKeys(rows, keys) {
    const numeric = new Set();
    if (!rows || rows.length === 0) return numeric;
    const sample = rows.slice(0, 80);
    keys.forEach(k => {
        let numericCount = 0, nonNullCount = 0;
        sample.forEach(r => {
            const v = r[k];
            if (v === null || v === undefined || v === '') return;
            nonNullCount++;
            if (typeof v === 'number' && !isNaN(v)) numericCount++;
            else if (typeof v === 'string' && v.trim() !== '' && !isNaN(parseFloat(v)) && isFinite(v)) numericCount++;
        });
        if (nonNullCount > 0 && numericCount / nonNullCount >= 0.8) numeric.add(k);
    });
    return numeric;
}

// Humanise a snake_case key for fallback labels
function humanise(k) {
    return String(k).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).replace(/\bPo\b/g, 'PO');
}

// ─── PIVOT COMPUTATION ──────────────────────────────────────────────────────────

function computePivot(data, config, customOrders = {}, extraRowValues = {}) {
    const { rowFields, colFields, filters, filterFields } = config;
    // Only apply filters for fields currently in the Filters area. Iterating
    // Object.entries(filters) would honor stale entries from fields the user
    // has since dragged out — meaning filters keep applying invisibly even
    // when the user thinks they've removed them.
    const activeFilters = (filterFields || [])
        .map(f => [f, filters?.[f]])
        .filter(([, vals]) => Array.isArray(vals) && vals.length > 0);
    const filtered = activeFilters.length === 0
        ? data
        : data.filter(row =>
            activeFilters.every(([field, vals]) => vals.includes(getVal(row, field)))
        );
    const hasRows = rowFields.length > 0;
    const hasCols = colFields.length > 0;
    const hasSubtotals = rowFields.length > 1;
    // extraRowValues: { fieldKey: [v1, v2, ...] } — values that should always
    // appear as row combos even when there's no underlying data. Only meaningful
    // for single-row-field pivots; for multi-level rows we don't know how to
    // complete the tuple, so we skip injection.
    const injectedRowValues = (rowFields.length === 1 && extraRowValues?.[rowFields[0]]) || null;

    // Single pass over `filtered` — builds row/col combos, the cell lookup,
    // and row/col/subtotal buckets in one traversal. Each row only needs
    // makeKey called once per axis instead of three times across passes.
    const rowComboMap = new Map();
    const colComboMap = new Map();
    const lookup = {}, rowTotal = {}, colTotal = {}, subtotals = {}, subtotalRows = {};

    for (let i = 0; i < filtered.length; i++) {
        const row = filtered[i];
        const rowKey = hasRows ? makeKey(row, rowFields) : '__all__';
        const colKey = hasCols ? makeKey(row, colFields) : '__total__';

        if (!rowComboMap.has(rowKey)) {
            rowComboMap.set(rowKey, hasRows ? rowFields.map(f => getVal(row, f)) : ['(All)']);
        }
        if (hasCols && !colComboMap.has(colKey)) {
            colComboMap.set(colKey, colFields.map(f => getVal(row, f)));
        }

        let bucket = lookup[rowKey];
        if (!bucket) bucket = lookup[rowKey] = {};
        (bucket[colKey] || (bucket[colKey] = [])).push(row);

        (rowTotal[rowKey] || (rowTotal[rowKey] = [])).push(row);
        if (hasCols) (colTotal[colKey] || (colTotal[colKey] = [])).push(row);

        if (hasSubtotals) {
            const lvl0 = getVal(row, rowFields[0]);
            let sub = subtotals[lvl0];
            if (!sub) sub = subtotals[lvl0] = {};
            (sub[colKey] || (sub[colKey] = [])).push(row);
            (subtotalRows[lvl0] || (subtotalRows[lvl0] = [])).push(row);
        }
    }

    // Inject configured values (e.g. all TNA stages from config) that didn't
    // appear in `filtered`, so those rows still render with empty cells.
    if (injectedRowValues) {
        for (const v of injectedRowValues) {
            if (v === null || v === undefined) continue;
            const key = String(v);
            if (!rowComboMap.has(key)) rowComboMap.set(key, [v]);
        }
    }
    const rowCombos = sortCombos([...rowComboMap.values()], rowFields, customOrders);
    const colCombos = hasCols ? sortCombos([...colComboMap.values()], colFields, customOrders) : null;

    // grandTotalRows is identical to `filtered` — reuse the reference instead
    // of cloning N rows into a new array.
    return { filtered, rowCombos, colCombos, lookup, rowTotal, colTotal, subtotals, subtotalRows, grandTotalRows: filtered };
}

// ─── TABLE STRUCTURE BUILDER ────────────────────────────────────────────────────

function buildTableStructure(pivotData, config, fieldLabel, valueSort = null) {
    const { rowCombos, colCombos, lookup, rowTotal, colTotal, subtotals, subtotalRows, grandTotalRows } = pivotData;
    const { rowFields, colFields, valueFields, showSubtotals, showGrandTotal } = config;
    const hasCols = colFields.length > 0;
    const numVals = valueFields.length;
    const numRowCols = Math.max(rowFields.length, 1);

    // ── Value-based row sort ────────────────────────────────────────────────
    // When the user clicks a value-field header, reorder rows by that field's
    // value. `valueSort.colKey` decides WHICH column drives the sort:
    //   '__grand__' (or absent) → the row's grand total across all columns
    //   a colCombo key          → only that column's cell value for the row
    // nulls/empties always sink to the bottom regardless of direction. For
    // multi-level rows we sort the top-level groups by their (column-scoped)
    // subtotal, then sort each group's rows — keeping groups contiguous.
    const sortVf = valueSort && valueFields.find(v => v.field === valueSort.field);
    function applyValueSort(combos) {
        if (!sortVf) return combos;
        const dir = valueSort.dir === 'desc' ? -1 : 1;
        const colKey = valueSort.colKey;
        const isGrand = !colKey || colKey === '__grand__';
        const keyFor = (rows) => {
            const v = aggregate(rows, sortVf.field, sortVf.aggFunc);
            return v === null || v === undefined || (typeof v === 'number' && isNaN(v)) ? null : v;
        };
        const cmpNum = (a, b) => {
            if (a === null && b === null) return 0;
            if (a === null) return 1;   // nulls last
            if (b === null) return -1;
            return (a - b) * dir;
        };
        const comboKey = (combo) => {
            const rowKey = combo[0] === '(All)' ? '__all__'
                : (rowFields.length > 0 ? combo.join('\x00') : '__all__');
            const rows = isGrand ? (rowTotal[rowKey] || []) : ((lookup[rowKey] && lookup[rowKey][colKey]) || []);
            return keyFor(rows);
        };
        if (rowFields.length <= 1) {
            return [...combos].sort((x, y) => cmpNum(comboKey(x), comboKey(y)));
        }
        const groupKey = (lvl0) => {
            const rows = isGrand ? (subtotalRows[lvl0] || []) : ((subtotals[lvl0] && subtotals[lvl0][colKey]) || []);
            return keyFor(rows);
        };
        return [...combos].sort((x, y) => {
            const g = cmpNum(groupKey(x[0]), groupKey(y[0]));
            if (g !== 0) return g;
            if (x[0] !== y[0]) return x[0] < y[0] ? -1 : 1; // keep ties' groups contiguous
            return cmpNum(comboKey(x), comboKey(y));
        });
    }
    const orderedRowCombos = applyValueSort(rowCombos);

    const headerRows = [];

    if (!hasCols) {
        const cells = rowFields.map(f => ({ content: fieldLabel(f), cls: 'pivot-th-row' }));
        valueFields.forEach(vf => cells.push({
            // content: `${AGGREGATIONS[vf.aggFunc].label} of ${fieldLabel(vf.field)}`,
            content: fieldLabel(vf.field),
            cls: 'pivot-th-val',
            sortField: vf.field,
            sortColKey: '__grand__',
        }));
        headerRows.push(cells);
    } else {
        // Build a hierarchical header for the columns area: one row per col
        // field level, with consecutive combos sharing the same parent value
        // merged via colspan (Excel-style). The redundant value-label row is
        // skipped when there's only one value field — the column header alone
        // is enough.
        const colLevels = [];
        for (let level = 0; level < colFields.length; level++) {
            const groups = [];
            let i = 0;
            while (i < colCombos.length) {
                const value = colCombos[i][level];
                const prefix = colCombos[i].slice(0, level);
                let j = i + 1;
                while (
                    j < colCombos.length &&
                    colCombos[j][level] === value &&
                    prefix.every((v, idx) => colCombos[j][idx] === v)
                ) j++;
                groups.push({ value, span: j - i });
                i = j;
            }
            colLevels.push(groups);
        }

        const hasValueLabelRow = numVals > 1;
        const totalHeaderRows = colFields.length + (hasValueLabelRow ? 1 : 0);

        // First row — row-field labels (spanning all header rows) + Grand Total
        // (leftmost, before the col levels) + top col level.
        const firstRow = rowFields.map(f => ({
            content: fieldLabel(f),
            cls: 'pivot-th-row',
            rowspan: totalHeaderRows,
        }));
        if (showGrandTotal) {
            // Grand Total sits at the START of the column area. Spans the
            // column-field levels; the bottom value-label row gets its own GT
            // entries when multiple value fields are configured.
            firstRow.push({
                content: 'Grand Total',
                cls: 'pivot-th-grand',
                colspan: numVals,
                rowspan: colFields.length,
                // With a single value field there's no value-label row to host
                // the sort control, so the Grand Total header itself sorts rows
                // by that value's overall total.
                ...(numVals === 1 ? { sortField: valueFields[0].field, sortColKey: '__grand__' } : {}),
            });
        }
        colLevels[0].forEach(g => firstRow.push({
            content: g.value,
            cls: 'pivot-th-col',
            colspan: g.span * numVals,
        }));
        headerRows.push(firstRow);

        // Lower column-field levels.
        for (let level = 1; level < colFields.length; level++) {
            const row = [];
            colLevels[level].forEach(g => row.push({
                content: g.value,
                cls: 'pivot-th-col',
                colspan: g.span * numVals,
            }));
            headerRows.push(row);
        }

        // Value-label row — only when multiple value fields are configured.
        if (hasValueLabelRow) {
            const row = [];
            if (showGrandTotal) {
                valueFields.forEach(vf => row.push({
                    content: fieldLabel(vf.field),
                    cls: 'pivot-th-val',
                    // Sort rows by this value's grand total (across all columns).
                    sortField: vf.field,
                    sortColKey: '__grand__',
                }));
            }
            for (let c = 0; c < colCombos.length; c++) {
                const colKey = colCombos[c].join('\x00');
                valueFields.forEach(vf => row.push({
                    content: fieldLabel(vf.field),
                    cls: 'pivot-th-val',
                    // Sort rows by this value within THIS specific column.
                    sortField: vf.field,
                    sortColKey: colKey,
                }));
            }
            headerRows.push(row);
        }
    }

    const bodyRows = [];

    function cellVal(rows, vf) {
        const v = aggregate(rows, vf.field, vf.aggFunc);
        return {
            content: v !== null ? formatNum(v, vf.aggFunc, vf.field) : '',
            rawValue: v,
        };
    }

    // Per-cell PO-count breakdown: on-track / delayed sums + their ratio.
    // ratio === null when there's no po_count signal at all → cell stays neutral.
    function trackBreakdown(rows) {
        if (!rows || rows.length === 0) return { onTrack: 0, delayed: 0, total: 0, ratio: null };
        let onTrack = 0, delayed = 0, total = 0;
        for (const r of rows) {
            onTrack += Number(r.on_track_po_count) || 0;
            delayed += Number(r.delayed_po_count) || 0;
            total += Number(r.po_count) || 0;
        }
        return { onTrack, delayed, total, ratio: total > 0 ? onTrack / total : null };
    }

    function valueCells(rowsForCols, rowsForTotal, opts = {}, rowCombo = null) {
        const cells = [];
        const grand = trackBreakdown(rowsForTotal);
        if (hasCols) {
            if (showGrandTotal) {
                valueFields.forEach(vf => cells.push({
                    ...cellVal(rowsForTotal, vf),
                    ...opts,
                    cls: (opts.cls || '') + ' pivot-td-grand',
                    field: vf.field,
                    rows: rowsForTotal,
                    rowCombo,
                    colCombo: null,
                    onTrackRatio: grand.ratio,
                    onTrackCount: grand.onTrack,
                    delayedCount: grand.delayed,
                }));
            }
            colCombos.forEach(colCombo => {
                const colKey = colCombo.join('\x00');
                const rows = rowsForCols[colKey] || [];
                const b = trackBreakdown(rows);
                valueFields.forEach(vf => cells.push({
                    ...cellVal(rows, vf),
                    ...opts,
                    field: vf.field,
                    rows,
                    rowCombo,
                    colCombo,
                    onTrackRatio: b.ratio,
                    onTrackCount: b.onTrack,
                    delayedCount: b.delayed,
                }));
            });
        } else {
            valueFields.forEach(vf => cells.push({
                ...cellVal(rowsForTotal, vf),
                ...opts,
                field: vf.field,
                rows: rowsForTotal,
                rowCombo,
                colCombo: null,
                onTrackRatio: grand.ratio,
                onTrackCount: grand.onTrack,
                delayedCount: grand.delayed,
            }));
        }
        return cells;
    }

    if (rowFields.length <= 1) {
        orderedRowCombos.forEach(combo => {
            const rowKey = combo[0] === '(All)' ? '__all__' : (rowFields.length > 0 ? combo.join('\x00') : '__all__');
            const cells = [{ content: combo.join(' › '), cls: 'pivot-td-row' }];
            cells.push(...valueCells(lookup[rowKey] || {}, rowTotal[rowKey] || [], {}, combo));
            bodyRows.push({ cells, type: 'data' });
        });
    } else {
        const groups = {}, groupOrder = [];
        orderedRowCombos.forEach(combo => {
            const lvl0 = combo[0];
            if (!groups[lvl0]) { groups[lvl0] = []; groupOrder.push(lvl0); }
            groups[lvl0].push(combo);
        });

        groupOrder.forEach(lvl0 => {
            const groupCombos = groups[lvl0];
            const rowspan = groupCombos.length;

            groupCombos.forEach((combo, idx) => {
                const rowKey = combo.join('\x00');
                const cells = [];
                if (idx === 0) cells.push({ content: lvl0, rowspan, cls: 'pivot-td-group' });
                for (let l = 1; l < rowFields.length; l++) cells.push({ content: combo[l], cls: 'pivot-td-row' });
                cells.push(...valueCells(lookup[rowKey] || {}, rowTotal[rowKey] || [], {}, combo));
                bodyRows.push({ cells, type: 'data' });
            });

            if (showSubtotals) {
                const cells = [{ content: `${lvl0} Total`, colspan: numRowCols, cls: 'pivot-td-subtotal' }];
                cells.push(...valueCells(subtotals[lvl0] || {}, subtotalRows[lvl0] || [], { cls: 'pivot-td-subtotal' }, [lvl0]));
                bodyRows.push({ cells, type: 'subtotal' });
            }
        });
    }

    if (showGrandTotal) {
        // Only the left-most "Grand Total" label cell needs `pivot-td-grand-row`
        // (which `tdClass` reads as "sticky left, text-left"). Value cells are
        // numeric — they pick up alignment + grand-total styling from the
        // `rowType === 'grandtotal'` branch.
        const cells = [{ content: 'Grand Total', colspan: numRowCols, cls: 'pivot-td-grand-row' }];
        if (hasCols) {
            // Grand Total cells (overall) sit at the START, before per-column totals.
            const gt = trackBreakdown(grandTotalRows);
            valueFields.forEach(vf => cells.push({
                ...cellVal(grandTotalRows, vf),
                cls: 'pivot-td-grand',
                field: vf.field,
                rows: grandTotalRows,
                rowCombo: null,
                colCombo: null,
                onTrackRatio: gt.ratio,
                onTrackCount: gt.onTrack,
                delayedCount: gt.delayed,
            }));
            colCombos.forEach(colCombo => {
                const colKey = colCombo.join('\x00');
                const rows = colTotal[colKey] || [];
                const b = trackBreakdown(rows);
                valueFields.forEach(vf => cells.push({
                    ...cellVal(rows, vf),
                    field: vf.field,
                    rows,
                    rowCombo: null,
                    colCombo,
                    onTrackRatio: b.ratio,
                    onTrackCount: b.onTrack,
                    delayedCount: b.delayed,
                }));
            });
        } else {
            const gt = trackBreakdown(grandTotalRows);
            valueFields.forEach(vf => cells.push({
                ...cellVal(grandTotalRows, vf),
                field: vf.field,
                rows: grandTotalRows,
                rowCombo: null,
                colCombo: null,
                onTrackRatio: gt.ratio,
                onTrackCount: gt.onTrack,
                delayedCount: gt.delayed,
            }));
        }
        // Grand Total row sits at the TOP of the body, before data rows.
        bodyRows.unshift({ cells, type: 'grandtotal' });
    }

    return { headerRows, bodyRows, rowFields, colFields };
}

// ─── PIVOT GRID RENDERER ────────────────────────────────────────────────────────

// Progressive rendering tuning — keeps the initial paint cheap even for 10k-row
// pivots, then quietly streams more rows in as the user scrolls. These are the
// only knobs you need to tune perf:
//   INITIAL  = rows mounted on first paint
//   STEP     = rows added each time the scroll nears the bottom
//   TRIGGER  = px from bottom at which we load the next chunk
const PROGRESSIVE_INITIAL = 250;
const PROGRESSIVE_STEP = 250;
const PROGRESSIVE_TRIGGER = 600;

// Approximate header-row height. Each header row's `top` is stacked using this
// so multiple header rows don't overlap each other when stuck — instead they
// sit one below the other (like Excel's frozen rows).
// padding-y 0.625rem (10px top + 10px bottom = 20px) + 11.5px text + ~4px line spacing + 1px border ≈ 36px
const HEADER_ROW_HEIGHT = 36;

// z-index layering for sticky cells:
//   5 = top-left corner (sticky on BOTH axes — must beat everything)
//   4 = sticky-top header cells
//   3 = sticky-left body cells (row labels)
//   1 = regular body cells
//
// These helpers are hoisted to module scope (instead of defined inside
// PivotGrid) so the function references stay stable across renders — that's
// what lets React.memo on PivotRow actually short-circuit when scrolling
// expands the render window. Defining them in render would create fresh
// closures every commit, defeating the memo and re-rendering every row.
function thClass(cell) {
    const base = 'px-3 py-2.5 text-[11.5px] font-semibold whitespace-nowrap sticky border-b border-r border-[#E5E7EB]';
    if (cell.cls?.includes('pivot-th-row')) {
        return `${base} bg-[#F9FAFB] text-[#0F172A] text-left z-[5] border-r border-[#E5E7EB] left-0`;
    }
    if (cell.cls?.includes('pivot-th-grand')) {
        return `${base} bg-[#F9FAFB] text-[#0F172A] text-right z-[4] border-l border-[#E5E7EB]`;
    }
    if (cell.cls?.includes('pivot-th-col')) {
        return `${base} bg-[#F9FAFB] text-[#0F172A] text-center z-[4]`;
    }
    if (cell.cls?.includes('pivot-th-val')) {
        return `${base} bg-[#F9FAFB] text-[#64748B] text-right z-[4] font-normal`;
    }
    return `${base} bg-[#F9FAFB] text-[#0F172A] z-[4]`;
}

// Value cells where on-track ratio is known get a green/red tint that overrides
// the default bg. Skipped for label/group cells, and when the cell has no data.
function valueTintClass(cell) {
    if (!VALUES_ONLY_FIELDS.includes(cell.field)) return '';
    if (cell.content === '' || cell.content === null || cell.content === undefined) return '';
    if (cell.onTrackRatio === null || cell.onTrackRatio === undefined) return '';
    return RATIO_TINT[ratioTier(cell.onTrackRatio)];
}

function tdClass(cell, rowType) {
    const isRowLabel = cell.cls?.includes('pivot-td-row')
        || cell.cls?.includes('pivot-td-group')
        || cell.cls?.includes('pivot-td-grand-row')
        || cell.cls?.includes('pivot-td-subtotal');
    const base = `px-3 py-2 text-[12px] whitespace-nowrap border-b border-r border-[#E5E7EB] ${isRowLabel ? 'text-left' : 'text-right tabular-nums'}`;

    if (cell.cls?.includes('pivot-td-group')) {
        return `${base} bg-white text-[#0F172A] font-semibold align-top border-r border-[#E5E7EB] sticky left-0 z-[3]`;
    }
    if (cell.cls?.includes('pivot-td-row')) {
        return `${base} text-[#1E293B] bg-white sticky left-0 z-[3]`;
    }
    if (cell.cls?.includes('pivot-td-grand-row')) {
        return `${base} bg-[#F9FAFB] text-[#0F172A] font-semibold border-t border-[#E5E7EB] sticky left-0 z-[3]`;
    }
    if (cell.cls?.includes('pivot-td-subtotal')) {
        const tint = valueTintClass(cell);
        return `${base} ${tint || 'bg-[#FAFBFC] text-[#0F172A]'} font-semibold sticky left-0 z-[3]`;
    }

    // Grand Total (row & column) keeps the neutral gray — only data/subtotal
    // cells get the on-track tint.
    if (rowType === 'grandtotal') {
        return `${base} bg-[#F9FAFB] text-[#0F172A] font-semibold border-t border-[#E5E7EB]`;
    }
    if (cell.cls?.includes('pivot-td-grand')) {
        return `${base} bg-[#F9FAFB] text-[#0F172A] font-semibold border-l border-[#E5E7EB]`;
    }
    const tint = valueTintClass(cell);
    if (rowType === 'subtotal') {
        return `${base} ${tint || 'bg-[#FAFBFC] text-[#0F172A]'} font-semibold`;
    }
    return `${base} ${tint || 'text-[#1E293B] bg-white'}`;
}

function renderCellContent(content) {
    if (content === '' || content === null || content === undefined) {
        return <span className="text-[#CBD5E1]">–</span>;
    }
    if (content === '(blank)') {
        return <span className="text-[#CBD5E1]">-</span>;
    }
    return content;
}

// Portal-standard sort arrow (lucide arrow-up/down-wide-narrow), matching the
// rest of the app's table headers. `dir` shows the active direction in the
// brand colour; unsorted-but-sortable shows a muted up arrow so EVERY value
// column reads as sortable (under each cluster, not just Grand Total).
function SortIndicator({ dir }) {
    if (dir === 'desc') {
        return <ArrowDownWideNarrow size={13} strokeWidth={2.2} className="ml-1 shrink-0 text-[#5d5fef]" />;
    }
    return (
        <ArrowUpWideNarrow
            size={13}
            strokeWidth={dir === 'asc' ? 2.2 : 1.8}
            className={`ml-1 shrink-0 ${dir === 'asc' ? 'text-[#5d5fef]' : 'text-[#94A3B8]'}`}
        />
    );
}

// Tooltip body for a PO Count cell: the on-track / delayed breakdown. Kept
// separate so the Tooltip can wrap the whole <td> (see PivotRow) rather than
// just the text — hovering anywhere in the cell reveals it.
function poCountTip(cell) {
    const fmt = (n) => Number(n || 0).toLocaleString('en-IN');
    return (
        <div className="text-[12px] leading-tight whitespace-nowrap">
            <div className="whitespace-nowrap">
                <span className="inline-block h-[6px] w-[6px] rounded-full bg-emerald-400 mr-1.5 align-middle" />
                On track: <span className="font-semibold">{fmt(cell.onTrackCount)}</span>
            </div>
            <div className="mt-1 whitespace-nowrap">
                <span className="inline-block h-[6px] w-[6px] rounded-full bg-red-400 mr-1.5 align-middle" />
                Delayed: <span className="font-semibold">{fmt(cell.delayedCount)}</span>
            </div>
        </div>
    );
}

// PO Count cells show just the total styled as a clickable link. Link color
// follows the cell's on-track ratio so it visually matches the red/green tint.
function renderPoCountContent(cell, clickable, isGrandTotal) {
    if (cell.content === '' || cell.content === null || cell.content === undefined) {
        return <span className="text-[#CBD5E1]">–</span>;
    }
    let linkClass = "inline-block";
    if (clickable) {
        if (isGrandTotal) {
            // Grand Total cells (row & column) keep the default black text.
            linkClass = "inline-block font-semibold underline underline-offset-2 decoration-[#CBD5E1] hover:decoration-[#0F172A]";
        } else {
            const r = cell.onTrackRatio;
            if (r === null || r === undefined) {
                linkClass = "inline-block text-[#2563EB] font-semibold underline underline-offset-2 decoration-[#93C5FD] hover:decoration-[#2563EB] hover:text-[#1D4ED8]";
            } else {
                linkClass = RATIO_LINK[ratioTier(r)];
            }
        }
    }
    return <span className={linkClass}>{cell.content}</span>;
}

// Render-row is memoised so that, when `renderLimit` grows, only the *newly
// added* rows mount — existing ones short-circuit React.memo's shallow check.
const PivotRow = React.memo(function PivotRow({ row }) {
    const { onCountClick } = useContext(PivotCellContext);
    return (
        <tr className={row.type === 'data' ? 'group transition-colors hover:bg-[#F8F9FF]' : 'group'}>
            {row.cells.map((cell, ci) => {
                const hasCount =
                    cell.field === 'po_count' &&
                    cell.content !== '' &&
                    cell.content !== null &&
                    cell.content !== undefined;
                const isClickableCount = hasCount && typeof onCountClick === 'function';
                const isGrandTotalCell = row.type === 'grandtotal' || !!cell.cls?.includes('pivot-td-grand');
                // Clickable count cells get an inset ring + slight emphasis on
                // hover so the whole cell reads as an interactive target.
                const hoverCls = isClickableCount ? isGrandTotalCell ? ' hover:ring-[#67768e]/50' : cell?.onTrackRatio < 0.70 ? ' hover:ring-[#f87171]/50' : ' hover:ring-[#34d399]/50' : '';
                const commonHover = isClickableCount ? ' cursor-pointer transition-shadow hover:ring-2 hover:ring-inset ' : '';
                const tdNode = (
                    <td
                        key={ci}
                        rowSpan={cell.rowspan || 1}
                        colSpan={cell.colspan || 1}
                        className={`${tdClass(cell, row.type)}${hoverCls}${commonHover}`}
                        onClick={isClickableCount ? () => onCountClick(cell) : undefined}
                    >
                        {cell.field === 'po_count'
                            ? renderPoCountContent(cell, isClickableCount, isGrandTotalCell)
                            : renderCellContent(cell.content)}
                    </td>
                );
                // Wrap the whole cell so the on-track / delayed breakdown shows on
                // hovering anywhere in the <td>, not just over the number.
                if (hasCount) {
                    return (
                        <Tooltip
                            key={ci}
                            title={poCountTip(cell)}
                            placement="top"
                            mouseEnterDelay={0.1}
                            align={{ offset: [0, -2] }}
                            overlayInnerStyle={{ whiteSpace: 'nowrap', padding: '8px 10px' }}
                        >
                            {tdNode}
                        </Tooltip>
                    );
                }
                // PO Quantity / PO Amount show short K/L/Cr notation in the cell;
                // reveal the exact Indian-formatted value on hover. Only when the
                // value is actually abbreviated — a plain number like 120 shows in
                // full, so a tooltip would just repeat it.
                const showFullValueTip =
                    (cell.field === 'po_quantity' || cell.field === 'po_amount') &&
                    isShortened(cell.rawValue);
                if (showFullValueTip) {
                    return (
                        <Tooltip
                            key={ci}
                            title={<span className="text-[12px] whitespace-nowrap">{formatFullIndian(cell.rawValue)}</span>}
                            placement="top"
                            mouseEnterDelay={0.1}
                            align={{ offset: [0, -2] }}
                            overlayInnerStyle={{ whiteSpace: 'nowrap', padding: '8px 10px' }}
                        >
                            {tdNode}
                        </Tooltip>
                    );
                }
                return tdNode;
            })}
        </tr>
    );
});

function PivotGrid({ headerRows, bodyRows, onSortValue, valueSort }) {
    const scrollRef = useRef(null);
    const [renderLimit, setRenderLimit] = useState(PROGRESSIVE_INITIAL);

    // Stash bodyRows in a ref so the scroll listener can read the latest total
    // without resubscribing — otherwise the effect re-bound on every renderLimit
    // change, causing dozens of add/remove listener cycles for big pivots.
    const bodyRowsRef = useRef(bodyRows);
    bodyRowsRef.current = bodyRows;

    // Reset the window every time the underlying data changes — otherwise the
    // user could be looking at a fresh, small pivot but with the render budget
    // from a previous huge one already burned.
    useEffect(() => {
        setRenderLimit(PROGRESSIVE_INITIAL);
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }, [bodyRows]);

    // Scroll listener — subscribed once. Reads the current bodyRows from a ref
    // so it stays correct after data changes without rebinding the listener.
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const onScroll = () => {
            const total = bodyRowsRef.current?.length || 0;
            const { scrollTop, scrollHeight, clientHeight } = el;
            if (scrollHeight - scrollTop - clientHeight < PROGRESSIVE_TRIGGER) {
                setRenderLimit(prev => (prev >= total ? prev : Math.min(prev + PROGRESSIVE_STEP, total)));
            }
        };
        el.addEventListener('scroll', onScroll, { passive: true });
        return () => el.removeEventListener('scroll', onScroll);
    }, []);

    // Adjust rowspans that would otherwise extend past the rendered window.
    // Without this, a group-header cell with rowspan=20 starting at row 240
    // would render a 20-row-tall cell when only 10 of its sub-rows exist in
    // the DOM — producing a visually broken oversized cell.
    const visibleRows = useMemo(() => {
        if (!bodyRows) return [];
        const limit = Math.min(renderLimit, bodyRows.length);
        if (limit >= bodyRows.length) return bodyRows;
        const sliced = bodyRows.slice(0, limit);
        return sliced.map((row, idx) => {
            const hasOverhang = row.cells.some(c => c.rowspan && c.rowspan > limit - idx);
            if (!hasOverhang) return row;
            return {
                ...row,
                cells: row.cells.map(c =>
                    c.rowspan && c.rowspan > limit - idx ? { ...c, rowspan: limit - idx } : c
                ),
            };
        });
    }, [bodyRows, renderLimit]);

    if (!headerRows || !bodyRows) return null;

    const moreCount = bodyRows.length - visibleRows.length;

    return (
        <div
            ref={scrollRef}
            className="overflow-auto flex-1 min-h-0 pivot-scroll-container"
            // scrollbarGutter reserves space for the scrollbar so the last column
            // (Grand Total) isn't covered by the scrollbar thumb on browsers where
            // the scrollbar takes up width (Chrome/Edge on Windows/Linux).
            style={{ scrollbarWidth: 'thin', scrollbarGutter: 'stable' }}
        >
            <style>{`
                .pivot-scroll-container::-webkit-scrollbar {
                    width: 8px;
                    height: 8px;
                }
                .pivot-scroll-container::-webkit-scrollbar-track {
                    background: #F9FAFB;
                }
                .pivot-scroll-container::-webkit-scrollbar-thumb {
                    background: #CBD5E1;
                    border-radius: 4px;
                    border: 2px solid #F9FAFB;
                }
                .pivot-scroll-container::-webkit-scrollbar-thumb:hover {
                    background: #94A3B8;
                }
                .pivot-scroll-container::-webkit-scrollbar-corner {
                    background: #F9FAFB;
                }
            `}</style>
            <table className="border-separate border-spacing-0 text-[12px] min-w-full bg-white">
                <thead>
                    {headerRows.map((row, ri) => (
                        <tr key={ri}>
                            {row.map((cell, ci) => {
                                const sortable = !!cell.sortField && typeof onSortValue === 'function';
                                const activeDir = sortable
                                    && valueSort?.field === cell.sortField
                                    && (valueSort?.colKey ?? '__grand__') === (cell.sortColKey ?? '__grand__')
                                    ? valueSort.dir : null;
                                return (
                                    <th
                                        key={ci}
                                        rowSpan={cell.rowspan || 1}
                                        colSpan={cell.colspan || 1}
                                        className={`${thClass(cell)}${sortable ? ' cursor-pointer select-none hover:text-[#5d5fef]' : ''}`}
                                        // Stack each header row below the previous one when sticky.
                                        // Without this, all rows have top:0 and overlap → user sees
                                        // the bottom row "scroll away" because it sits visually on top
                                        // of the top row when stuck.
                                        style={{ top: `${ri * HEADER_ROW_HEIGHT}px` }}
                                        onClick={sortable ? () => onSortValue(cell.sortField, cell.sortColKey) : undefined}
                                        title={sortable ? 'Click to sort rows by this value' : undefined}
                                    >
                                        {sortable ? (
                                            <span className="inline-flex items-center justify-end">
                                                {renderCellContent(cell.content)}
                                                <SortIndicator dir={activeDir} />
                                            </span>
                                        ) : (
                                            renderCellContent(cell.content)
                                        )}
                                    </th>
                                );
                            })}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {visibleRows.map((row, ri) => (
                        <PivotRow key={ri} row={row} />
                    ))}
                </tbody>
            </table>

            {/* Progressive-render footer. Streams more rows in on scroll, and a
                manual button is offered in case the scroll listener can't be
                triggered (e.g. content shorter than the viewport). */}
            {moreCount > 0 && (
                <div className="sticky bottom-0 left-0 right-0 flex items-center justify-between gap-3 border-t border-[#E5E7EB] bg-[#FAFBFC] px-4 py-2 text-[11px] text-[#64748B]">
                    <span>
                        Showing <span className="font-semibold text-[#0F172A]">{visibleRows.length.toLocaleString()}</span> of <span className="font-semibold text-[#0F172A]">{bodyRows.length.toLocaleString()}</span> rows
                        <span className="ml-2 text-[#94A3B8]">— more load as you scroll</span>
                    </span>
                    <button
                        type="button"
                        onClick={() => setRenderLimit(prev => Math.min(prev + PROGRESSIVE_STEP, bodyRows.length))}
                        className="rounded border border-[#D6D9E4] bg-white px-2 py-0.5 text-[11px] font-medium text-[#475569] hover:border-[#5d5fef] hover:text-[#5d5fef] transition-colors"
                    >
                        Load next {Math.min(PROGRESSIVE_STEP, moreCount).toLocaleString()}
                    </button>
                </div>
            )}
        </div>
    );
}

// ─── DRAG & DROP FIELD PANEL ────────────────────────────────────────────────────

const AREA_COLORS = {
    filters: { bg: '#fff7e6', border: '#ffd591', tag: 'orange', color: "#d46b08d4" },
    columns: { bg: '#eff6ff', border: '#bfdbfe', tag: 'blue', color: "#2563eb" },
    rows: { bg: '#f5f3ff', border: '#ddd6fe', tag: 'geekblue', color: "#7c3aed" },
    values: { bg: '#ecfdf5', border: '#a7f3d0', tag: 'green', color: "#059669" },
};

function FieldChip({ field, fieldLabelText, area, aggFunc, valueId, onRemove, onAggChange, draggable, onDragStart }) {
    const colors = AREA_COLORS[area] || {};
    // const label = area === 'values' ? `${AGGREGATIONS[aggFunc]?.label ?? aggFunc} of ${fieldLabelText}` : fieldLabelText;

    return (
        <>
            <div
                className="flex items-center gap-2 bg-indigo-50 px-2 py-2 mt-[5px] rounded"
                draggable={draggable}
                onDragStart={onDragStart}
                style={area === 'values' ? { background: colors.bg, borderColor: colors.border, color: colors.color } : {}}
            >
                <HolderOutlined className="text-indigo-600 text-[10px] cursor-grab" style={area === 'values' ? { color: colors.color } : {}} />
                <span className="flex-1 text-indigo-600 truncate font-medium text-[12px]" style={area === 'values' ? { color: colors.color } : {}}>{fieldLabelText}</span>
                {/* {area === 'values' && (
                    <Select
                        value={aggFunc}
                        size="small"
                        onChange={v => onAggChange(valueId, v)}
                        style={{ width: 78, fontSize: 11 }}
                        options={Object.entries(AGGREGATIONS).map(([k, v]) => ({ value: k, label: v.label }))}
                        onClick={e => e.stopPropagation()}
                        popupMatchSelectWidth={false}
                        className='rounded text-[10px] font-semibold text-slate-700 flex items-center gap-1'
                    />
                )} */}
                <CloseOutlined
                    className="opacity-60 hover:opacity-100 text-[13px] cursor-pointer text-indigo-500 hover:text-indigo-700"
                    onClick={() => onRemove(area, field, valueId)}
                    style={area === 'values' ? { color: colors.color } : {}}
                />
            </div>
        </>
    );
}

const getIcon = (type) => {
    let key = type?.toUpperCase?.();
    switch (key) {
        case "FILTERS": return (
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4B5563" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-funnel-icon lucide-funnel"><path d="M10 20a1 1 0 0 0 .553.895l2 1A1 1 0 0 0 14 21v-7a2 2 0 0 1 .517-1.341L21.74 4.67A1 1 0 0 0 21 3H3a1 1 0 0 0-.742 1.67l7.225 7.989A2 2 0 0 1 10 14z" /></svg>
        )
        case "COLUMNS": return (
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4B5563" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-columns3-icon lucide-columns-3"><rect width="18" height="18" x="3" y="3" rx="2" /><path d="M9 3v18" /><path d="M15 3v18" /></svg>
        )
        case "ROWS": return (
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4B5563" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-rows4-icon lucide-rows-4"><rect width="18" height="18" x="3" y="3" rx="2" /><path d="M21 7.5H3" /><path d="M21 12H3" /><path d="M21 16.5H3" /></svg>
        )
        case "VALUES": return (
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4B5563" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-chart-area-icon lucide-chart-area"><path d="M3 3v16a2 2 0 0 0 2 2h16" /><path d="M7 11.207a.5.5 0 0 1 .146-.353l2-2a.5.5 0 0 1 .708 0l3.292 3.292a.5.5 0 0 0 .708 0l4.292-4.292a.5.5 0 0 1 .854.353V16a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1z" /></svg>
        )
    }
}

function DropZone({ title, area, fields, valueFields: vfs, onDrop, onRemove, onAggChange, fieldLabel }) {
    const [dragOver, setDragOver] = useState(false);
    const colors = AREA_COLORS[area];

    const handleDragOver = e => { e.preventDefault(); setDragOver(true); };
    const handleDragLeave = () => setDragOver(false);
    const handleDrop = e => {
        e.preventDefault();
        setDragOver(false);
        try {
            const payload = JSON.parse(e.dataTransfer.getData('text/plain'));
            onDrop(area, payload);
        } catch { }
    };

    const items = area === 'values' ? vfs : (fields || []);

    return (
        <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            style={{
                // border: `1px dashed ${dragOver ? colors.border : '#EBEBEB'}`,
                background: dragOver ? colors.bg : 'white',
            }}
            // border-dashed border-slate-200 border
            className='rounded border border-dashed border-[#EBEBEB] bg-white p-2.5 shadow-sm'
        >
            <div className="flex items-center gap-2 mb-2">
                {getIcon(title)}
                <span className="text-[12px] tracking-[1px] font-semibold text-gray-600">
                    {title}
                </span>
            </div>
            {items.length === 0 && (
                <div className="italic text-gray-400 text-[14px]">Drop fields here</div>
            )}
            {area === 'values'
                ? vfs.map(vf => (
                    <FieldChip
                        key={vf.id}
                        field={vf.field}
                        fieldLabelText={fieldLabel(vf.field)}
                        area="values"
                        aggFunc={vf.aggFunc}
                        valueId={vf.id}
                        draggable
                        onDragStart={e => e.dataTransfer.setData('text/plain', JSON.stringify({ field: vf.field, fromArea: 'values', valueId: vf.id }))}
                        onRemove={onRemove}
                        onAggChange={onAggChange}
                    />
                ))
                : fields.map(f => (
                    <FieldChip
                        key={f}
                        field={f}
                        fieldLabelText={fieldLabel(f)}
                        area={area}
                        draggable
                        onDragStart={e => e.dataTransfer.setData('text/plain', JSON.stringify({ field: f, fromArea: area }))}
                        onRemove={onRemove}
                        onAggChange={onAggChange}
                    />
                ))
            }
        </div>
    );
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────────

let nextId = 10;

export default function TNAPivotTable(props) {
    // ── Redux / backend wiring ─────────────────────────────────────────────────
    const dispatch = useDispatch();
    const customToast = useCustomToast();
    const generalStore = useSelector(state => state.generalStore);
    const getTableComponentFifthListData = useSelector(state => state.generalStore?.getTableComponentFifthListData);
    const tnaGetAllStatusConfigData = useSelector(state => state?.administrationStore?.tnaGetAllStatusConfigData)
    const { bootConfig } = useContext(ReloadContext)

    const [headersData, setHeadersData] = useState({});
    const [headersType, setHeadersType] = useState("Default Headers");
    const [tnaDashboardData, setTnaDashboardData] = useState([]);
    const [tnaStageData, setTnaStageData] = useState([]);
    const [selectedData, setSelectedData] = useState(false);
    const [selectedModal, setSelectedModal] = useState(false);
    const isLoading = !!getTableComponentFifthListData?.isLoading;
    const [masterDbResponse, setMasterDbResponse] = useState({});
    const [filterData, setFilterData] = useState({})
    const [tnaDashRes, setTnaDashRes] = useState({})

    // ── Saved views ─────────────────────────────────────────
    // Persists `{ config, filterData }` per named view via the shared
    // get/createQuickFiltersRequest endpoints other modules use.
    const VIEW_DISPLAY_NAME = "TNA_DASHBOARD_DATA_V2";
    const getQuickFiltersData = useSelector(state => state.generalStore?.getQuickFiltersData);
    const createQuickFiltersData = useSelector(state => state.generalStore?.createQuickFiltersData);
    const getRefreshReportData = useSelector(state => state?.generalStore?.getRefreshReportData);
    const [savedViews, setSavedViews] = useState([]);
    const [activeViewName, setActiveViewName] = useState("");
    const [showSaveViewModal, setShowSaveViewModal] = useState(false);
    const [viewNameInput, setViewNameInput] = useState("");
    const [viewMenuOpenFor, setViewMenuOpenFor] = useState(null);
    const [showViewsDropdown, setShowViewsDropdown] = useState(false);
    const [saveModalMode, setSaveModalMode] = useState("new"); // "new" | "replace"
    const [replaceTargetName, setReplaceTargetName] = useState("");
    const [defaultAppliedRef] = useState(() => ({ current: false }));

    useEffect(() => {
        updateMasterDbResponse()
    }, [])

    // Function: To update the master db response
    const updateMasterDbResponse = async () => {
        const db = await openDB(getDataStorageKey?.(), 1);
        let updatedMasterDbResponse = {};
        // Await all reads before committing to state — otherwise setMasterDbResponse
        // (and db.close) run while the reads are still pending, leaving the config
        // groups empty and breaking the other_data hierarchical narrowing.
        await Promise.all(["DYNAMIC_ITEM_CONFIG", "DYNAMIC_SITE_CONFIG"].map(async key => {
            let response = await db.get("MASTER_DB_DROPDOWN", key) || [];
            updatedMasterDbResponse[key] = response;
        }))
        setMasterDbResponse(updatedMasterDbResponse)
        db?.close();
    }

    // Fetch headers on mount
    useEffect(() => {
        handleApiChange(dispatch, "getHeadersRequest", {
            enterpriseName: "TURNINGCLOUD",
            displayName: "TNA_DASHBOARD_DATA_V2",
            basedOn: "main",
        });
    }, [dispatch]);

    useEffect(() => {
        dispatchHandler(dispatch, "tnaGetAllStatusConfigRequest", {}, getPermissionKey("dashboard", "tna", "view"))
    }, [])

    useEffect(() => {
        if (tnaGetAllStatusConfigData?.isSuccess) {
            setTnaStageData(tnaGetAllStatusConfigData?.data?.resource ?? []);
        }
    }, [tnaGetAllStatusConfigData?.isSuccess])

    // ── Saved-views: fetch on mount, then refetch after every save/delete ──
    useEffect(() => {
        handleApiChange(dispatch, 'getQuickFiltersRequest', {
            attributeType: VIEW_DISPLAY_NAME,
            displayName: VIEW_DISPLAY_NAME,
            filterName: ""
        });
    }, []);

    useEffect(() => {
        if (getQuickFiltersData?.isSuccess) {
            const list = getQuickFiltersData?.data?.resource || [];
            setSavedViews(list);
            // If a saved view is marked default and we haven't applied anything
            // yet, apply it once on mount.
            if (!defaultAppliedRef.current && !activeViewName) {
                const def = list.find(v => v?.is_default === 1);
                if (def) {
                    defaultAppliedRef.current = true;
                    applyView(def);
                }
            }
        }
    }, [getQuickFiltersData?.isSuccess]);

    useEffect(() => {
        if (createQuickFiltersData?.isSuccess) {
            setShowSaveViewModal(false);
            setViewNameInput("");
            setReplaceTargetName("");
            setSaveModalMode("new");
            setTimeout(() => {
                dispatchHandler(dispatch, 'getQuickFiltersRequest', {
                    attributeType: VIEW_DISPLAY_NAME,
                    displayName: VIEW_DISPLAY_NAME,
                    filterName: ""
                }, getPermissionKey("dashboard", "tna", "view"));
            }, 500);
        }
    }, [createQuickFiltersData?.isSuccess]);

    const applyView = (view) => {
        const val = view?.filterValue;
        if (!val) return;
        setActiveViewName(view.filterName || "");
        if (val.config) {
            // Re-id valueFields so internal drag/drop keys stay unique.
            const valueFields = (val.config.valueFields || []).map(vf => ({
                ...vf, id: ++nextId,
            }));
            setConfig(prev => ({
                ...prev,
                rowFields: val.config.rowFields || prev.rowFields,
                colFields: val.config.colFields || prev.colFields,
                filterFields: val.config.filterFields || prev.filterFields,
                valueFields: valueFields.length ? valueFields : prev.valueFields,
                filters: val.config.filters || prev.filters,
                showSubtotals: val.config.showSubtotals ?? prev.showSubtotals,
                showGrandTotal: val.config.showGrandTotal ?? prev.showGrandTotal,
            }));
            // Mark init done so DEFAULT_PREFERENCES doesn't overwrite this view.
            initializedRef.current = true;
        }
        setFilterData(val.filterData || {});
    };

    const openSaveModal = () => {
        setViewNameInput("");
        setReplaceTargetName("");
        setSaveModalMode("new");
        setShowSaveViewModal(true);
    };

    const handleSaveView = () => {
        const isReplace = saveModalMode === "replace";
        const name = isReplace
            ? (replaceTargetName || "").trim()
            : (viewNameInput || "").trim();
        if (!name) return;
        // In "new" mode, if a view with this name already exists, switch the
        // modal into replace mode pre-filled with the conflicting name and let
        // the user confirm before overwriting.
        if (!isReplace && savedViews.some(v => v?.filterName === name)) {
            setSaveModalMode("replace");
            setReplaceTargetName(name);
            return;
        }
        const payload = {
            displayName: VIEW_DISPLAY_NAME,
            filterName: [name],
            filterValue: {
                config: {
                    rowFields: config.rowFields,
                    colFields: config.colFields,
                    filterFields: config.filterFields,
                    valueFields: config.valueFields.map(vf => ({ field: vf.field, aggFunc: vf.aggFunc })),
                    filters: config.filters,
                    showSubtotals: config.showSubtotals,
                    showGrandTotal: config.showGrandTotal,
                },
                filterData,
            },
            is_default: undefined,
        };
        dispatchHandler(dispatch, 'createQuickFiltersRequest', payload,
            getPermissionKey("common", "commonFunctionality", "quickfilters"));
    };

    const handleDeleteView = (view) => {
        if (createQuickFiltersData?.isLoading) return;
        dispatchHandler(dispatch, 'createQuickFiltersRequest', {
            displayName: VIEW_DISPLAY_NAME,
            filterName: [view.filterName],
            filterValue: view.filterValue,
            isDeleteFilter: true,
        }, getPermissionKey("common", "commonFunctionality", "quickfilters"));
        if (activeViewName === view.filterName) setActiveViewName("");
    };

    const handleMarkDefaultView = (view) => {
        if (createQuickFiltersData?.isLoading) return;
        dispatchHandler(dispatch, 'createQuickFiltersRequest', {
            displayName: VIEW_DISPLAY_NAME,
            filterName: [view.filterName],
            filterValue: view.filterValue,
            is_default: view.is_default === 1 ? 0 : 1,
        }, getPermissionKey("common", "commonFunctionality", "quickfilters"));
    };

    // Close the view-three-dot menu on any outside click.
    useEffect(() => {
        if (!viewMenuOpenFor) return;
        const close = () => setViewMenuOpenFor(null);
        document.addEventListener('click', close);
        return () => document.removeEventListener('click', close);
    }, [viewMenuOpenFor]);

    // Close the saved-views dropdown on outside click.
    useEffect(() => {
        if (!showViewsDropdown) return;
        const close = (e) => {
            const el = document.getElementById('tna-views-dropdown');
            if (el && !el.contains(e.target)) {
                setShowViewsDropdown(false);
                setViewMenuOpenFor(null);
            }
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [showViewsDropdown]);

    // Apply headers + data when they resolve
    useEffect(() => {
        if (generalStore?.getHeadersData?.isSuccess && generalStore?.getHeadersData?.data?.basedOn === "main") {
            const data = generalStore?.getHeadersData?.data?.resource || {};
            const type = Object.keys(data?.["Custom Headers"] || {}).length > 0 ? "Custom Headers" : "Default Headers";
            setHeadersData(data);
            setHeadersType(type);
        }
        if (getTableComponentFifthListData?.isSuccess) {
            setTnaDashRes(getTableComponentFifthListData?.data ?? {})
            setTnaDashboardData(getTableComponentFifthListData?.data?.resource || []);
        }
    }, [generalStore?.getHeadersData?.isSuccess, getTableComponentFifthListData?.isSuccess]);

    // ── Resolve active dataset, field map & numeric detection ─────────────────
    const activeData = useMemo(() => {
        return tnaDashboardData;
        // return Array.isArray(propsData) ? propsData : [];
    }, [tnaDashboardData]);

    // Field map: { snake_case_key: "Display Label" }
    // Strictly sourced from headersData[headersType] — only keys the backend knows about
    // are draggable and can be sent in the request payload.
    const fieldMap = useMemo(
        () => headersData?.[headersType] || {},
        [headersData, headersType]
    );

    // Field universe = headersData keys ∪ known measure columns ∪ any extras
    // the response returns that aren't declared elsewhere. The known measures
    // (po_quantity / po_count / po_amount) stay in the universe even when the
    // current response is empty — otherwise a refetch that briefly returns 0
    // rows would let the pruning effect strip them out of valueFields.
    const allFieldKeys = useMemo(() => {
        const fromHeaders = Object.keys(fieldMap);
        const seen = new Set(fromHeaders);
        const keys = [...fromHeaders];
        VALUES_ONLY_FIELDS.forEach(f => {
            if (!seen.has(f)) { seen.add(f); keys.push(f); }
        });
        return keys;
    }, [fieldMap]);

    const numericFields = useMemo(
        () => detectNumericKeys(activeData, allFieldKeys),
        [activeData, allFieldKeys]
    );

    const fieldLabel = useCallback(key => fieldMap[key] || humanise(key), [fieldMap]);

    // ── Pivot config state ─────────────────────────────────────────────────────
    const [config, setConfig] = useState(EMPTY_CONFIG);

    // Row sort driven by a value-field header click: { field, dir: 'asc'|'desc' }
    // or null for the natural (TNA-stage / alphabetical) order.
    const [valueSort, setValueSort] = useState(null);

    // useTransition lets us mark heavy state updates (anything that triggers a
    // pivot recompute) as non-urgent, so React keeps the UI thread responsive
    // — drag/drop ghost, filter typing, toggle clicks — while the new table
    // structure builds in the background.
    const [, startTransition] = useTransition();
    const setConfigDeferred = useCallback((updater) => {
        startTransition(() => setConfig(updater));
    }, []);

    // Cycle a value header through asc → desc → off. A sort is identified by
    // (field + column), so clicking PO Quantity under one column is distinct
    // from the same field under another. Wrapped in a transition so the
    // (potentially heavy) re-sort builds in the background and the click stays
    // responsive.
    const handleSortValue = useCallback((field, colKey = '__grand__') => {
        const key = colKey ?? '__grand__';
        startTransition(() => {
            setValueSort(prev => {
                const same = prev && prev.field === field && (prev.colKey ?? '__grand__') === key;
                if (!same) return { field, colKey: key, dir: 'asc' };
                if (prev.dir === 'asc') return { field, colKey: key, dir: 'desc' };
                return null;
            });
        });
    }, []);

    const requestColumns = useMemo(() => {
        const valid = new Set(allFieldKeys);
        const keys = [...new Set([
            ...config.rowFields,
            ...config.colFields,
            // ...config.valueFields.map(vf => vf.field),
        ])].filter(k => valid.has(k));
        return keys.join(',');
    }, [config.rowFields, config.colFields, allFieldKeys]); // config.valueFields - remove from dependency array

    // measures = distinct aggregation funcs from the Values area (uppercased)
    const requestMeasures = useMemo(() => {
        if (config.valueFields.length === 0) return 'SUM';
        const aggs = [...new Set(config.valueFields.map(vf => (vf.aggFunc || 'sum').toUpperCase()))];
        return aggs.join(',');
    }, [config.valueFields]);

    // filter = field → "a,b,c" (multi) or "a" (single); skips empty fields
    const buildFilterPayload = useCallback((data) => {
        const payload = {};
        Object.entries(data || {}).forEach(([key, val]) => {
            if (val === null || val === undefined || val === "") return;
            if (typeof val === "object") {
                const keys = Object.keys(val);
                if (keys.length === 0) return;
                payload[key] = keys.join(",");
            } else {
                payload[key] = val;
            }
        });
        return payload;
    }, []);
    
    const deferredFilterData = useDeferredValue(filterData);
    const requestFilter = useMemo(() => buildFilterPayload(deferredFilterData), [deferredFilterData, buildFilterPayload]);

    // Stable dependency for the fetch effect — only refire when payload contents change
    const requestFilterKey = useMemo(() => JSON.stringify(requestFilter), [requestFilter]);

    // Re-fire the request whenever any of the above changes
    useEffect(() => {
        if (!requestColumns) return;
        dispatchHandler(dispatch, "getTableComponentFifthListRequest", {
            apiUrl: "/purchase/dashboard/tna/analysis/v2",
            baseUrl: CONFIG.SM_BACKEND_BASE_URL,
            displayName: "TNA_DASHBOARD_DATA_V2",
            columns: requestColumns,
            // measures: requestMeasures,
            filter: requestFilter,
        }, getPermissionKey("dashboard", "tna", "view"));
    }, [dispatch, requestColumns, requestMeasures, requestFilterKey]);

    // After a successful "Refresh Report", the backend has rebuilt the
    // materialized view — re-fetch the dashboard data so the table reflects the
    // refreshed numbers. Uses the same columns/filter as the live fetch effect.
    useEffect(() => {
        if (!getRefreshReportData?.isSuccess) return;
        if (!requestColumns) return;
        dispatchHandler(dispatch, "getTableComponentFifthListRequest", {
            apiUrl: "/purchase/dashboard/tna/analysis/v2",
            baseUrl: CONFIG.SM_BACKEND_BASE_URL,
            displayName: "TNA_DASHBOARD_DATA_V2",
            columns: requestColumns,
            filter: requestFilter,
        }, getPermissionKey("dashboard", "tna", "view"));
    }, [getRefreshReportData?.isSuccess]);

    const [showPanel, setShowPanel] = useState(true);
    const [fieldSearch, setFieldSearch] = useState('');
    const [showFilters, setShowFilters] = useState(true);
    const [showFiltersPopover, setShowFiltersPopover] = useState(false);
    const initializedRef = useRef(false);

    // Whenever the field schema changes (headers reload, switch Default ↔ Custom),
    // drop any fields from config that the backend no longer recognises so the UI
    // and payload stay in sync with the current header set. Also evicts measure
    // fields that may have leaked into rowFields/colFields from a stale init.
    useEffect(() => {
        if (allFieldKeys.length === 0) return;
        const valid = new Set(allFieldKeys);
        setConfig(prev => {
            const next = {
                ...prev,
                rowFields: prev.rowFields.filter(f => valid.has(f) && !VALUES_ONLY_FIELDS.includes(f)),
                colFields: prev.colFields.filter(f => valid.has(f) && !VALUES_ONLY_FIELDS.includes(f)),
                filterFields: prev.filterFields.filter(f => valid.has(f)),
                valueFields: prev.valueFields.filter(vf => valid.has(vf.field)),
                filters: Object.fromEntries(
                    Object.entries(prev.filters || {}).filter(([k]) => valid.has(k))
                ),
            };
            // Avoid an extra render when nothing actually changed
            if (
                next.rowFields.length === prev.rowFields.length &&
                next.colFields.length === prev.colFields.length &&
                next.filterFields.length === prev.filterFields.length &&
                next.valueFields.length === prev.valueFields.length &&
                Object.keys(next.filters).length === Object.keys(prev.filters || {}).length
            ) return prev;
            return next;
        });
    }, [allFieldKeys]);

    // Computes the default config from DEFAULT_PREFERENCES (with name-pattern
    // fallback). Returns null when fields aren't loaded yet OR no usable value
    // measure is available — caller should leave config untouched in that case.
    const computeDefaultConfig = useCallback(() => {
        if (allFieldKeys.length === 0) return null;
        const hasDimension = allFieldKeys.some(k => !VALUES_ONLY_FIELDS.includes(k));
        if (!hasDimension) return null;

        const fieldSet = new Set(allFieldKeys);
        const isMeasure = (k) => VALUES_ONLY_FIELDS.includes(k);
        const fieldLabelLower = (k) => fieldLabel(k).toLowerCase();
        const pickByPatterns = (patterns, excluded) => {
            for (const pat of patterns) {
                const hit = allFieldKeys.find(k =>
                    k !== excluded && !isMeasure(k) && (pat.test(k) || pat.test(fieldLabelLower(k)))
                );
                if (hit) return hit;
            }
            return null;
        };
        const row = DEFAULT_PREFERENCES.rows.find(k => fieldSet.has(k))
            || pickByPatterns(FALLBACK_ROW_PATTERNS, null)
            || allFieldKeys.find(k => !isMeasure(k))
            || allFieldKeys[0];
        const col = DEFAULT_PREFERENCES.cols.find(k => fieldSet.has(k) && k !== row)
            || pickByPatterns(FALLBACK_COL_PATTERNS, row)
            || allFieldKeys.find(k => k !== row && !isMeasure(k))
            || allFieldKeys.find(k => k !== row);
        const fromPrefVals = DEFAULT_PREFERENCES.values.filter(v => fieldSet.has(v.field));
        let vals = fromPrefVals;
        if (vals.length === 0) {
            const fromPattern = allFieldKeys.find(k => FALLBACK_VAL_PATTERN.test(k));
            if (fromPattern) {
                vals = [{ field: fromPattern, aggFunc: numericFields.has(fromPattern) ? 'sum' : 'count' }];
            } else {
                const firstNumeric = allFieldKeys.find(k => numericFields.has(k));
                if (firstNumeric) vals = [{ field: firstNumeric, aggFunc: 'sum' }];
            }
        }
        if (vals.length === 0) return null;
        return {
            ...EMPTY_CONFIG,
            rowFields: row ? [row] : [],
            colFields: col ? [col] : [],
            valueFields: vals.map(v => ({ id: ++nextId, field: v.field, aggFunc: v.aggFunc })),
        };
    }, [allFieldKeys, numericFields, fieldLabel]);

    // Once headers are available, populate the initial config from DEFAULT_PREFERENCES.
    // Runs ONCE — never overrides the user's edits.
    useEffect(() => {
        if (initializedRef.current) return;
        const defaults = computeDefaultConfig();
        if (!defaults) return;
        setConfig(prev => {
            const next = { ...prev };
            let changed = false;
            if (prev.rowFields.length === 0 && defaults.rowFields.length) { next.rowFields = defaults.rowFields; changed = true; }
            if (prev.colFields.length === 0 && defaults.colFields.length) { next.colFields = defaults.colFields; changed = true; }
            if (prev.valueFields.length === 0 && defaults.valueFields.length) { next.valueFields = defaults.valueFields; changed = true; }
            return changed ? next : prev;
        });
        initializedRef.current = true;
    }, [computeDefaultConfig]);

    // Clear the active view and reset the dashboard to the DEFAULT_PREFERENCES
    // config — same logic as initial mount, recomputed against current headers.
    const resetToInitialState = () => {
        setActiveViewName("");
        setShowViewsDropdown(false);
        setFilterData({});
        setValueSort(null);
        const defaults = computeDefaultConfig();
        setConfig(defaults || EMPTY_CONFIG);
    };

    // ── Derivations ────────────────────────────────────────────────────────────
    const usedFields = useMemo(() => new Set([
        ...config.rowFields,
        ...config.colFields,
        ...config.valueFields.map(vf => vf.field),
        ...config.filterFields,
    ]), [config]);

    const availableFields = useMemo(() =>
        // !usedFields.has(f) &&
        allFieldKeys.filter(f => fieldLabel(f).toLowerCase().includes(fieldSearch.toLowerCase())),
        [allFieldKeys, fieldSearch, fieldLabel]
    );

    const customOrders = useMemo(() => {
        const order = { 'NOT STARTED': 0 };
        (tnaStageData || []).forEach((s, idx) => {
            if (s?.statusName) order[s.statusName] = s.statusOrder ?? idx + 1;
        });
        return { tna_status: order };
    }, [tnaStageData]);

    // Always-present row values: every configured TNA stage shows as a row
    // even when no data falls into it, keyed under whichever TNA-stage field
    // the backend exposes in this dataset.
    const extraRowValues = useMemo(() => {
        const stages = (tnaStageData || [])
            .filter(s => s?.statusName)
            .sort((a, b) => (a.statusOrder ?? 0) - (b.statusOrder ?? 0))
            .map(s => s.statusName);
        if (stages.length === 0) return {};
        const fieldSet = new Set(allFieldKeys);
        const stageField = TNA_STAGE_FIELD_CANDIDATES.find(k => fieldSet.has(k));
        return stageField ? { [stageField]: stages } : {};
    }, [tnaStageData, allFieldKeys]);

    // computePivot only reads rowFields/colFields/filterFields/filters from
    // config — narrow the deps so toggling subtotals/grand-total or swapping
    // valueFields/aggregations doesn't trigger a full re-pivot.
    const pivotData = useMemo(
        () => computePivot(activeData, config, customOrders, extraRowValues),
        [activeData, config.rowFields, config.colFields, config.filterFields, config.filters, customOrders, extraRowValues]
    );

    const tableStructure = useMemo(() => {
        if (!pivotData || config.valueFields.length === 0 || config.rowFields.length === 0) return null;
        return buildTableStructure(pivotData, config, fieldLabel, valueSort);
    }, [pivotData, config, fieldLabel, valueSort]);

    // Defer the heavy table render so config interactions (drag, filter, switch
    // toggles) stay responsive on big datasets. React renders against the *stale*
    // structure while the new one builds in the background, then atomically swaps.
    const deferredTableStructure = useDeferredValue(tableStructure);
    const isStaleRender = deferredTableStructure !== tableStructure;

    // Fields the user has dragged into the pivot that the response has no values for.
    // Usually means: backend didn't include that column in its response (or the key in
    // headersData doesn't match the key in the response payload).
    const missingFields = useMemo(() => {
        if (activeData.length === 0) return [];
        const responseKeys = new Set(Object.keys(activeData[0] || {}));
        const dragged = [
            ...config.rowFields,
            ...config.colFields,
            ...config.filterFields,
            ...config.valueFields.map(vf => vf.field),
        ];
        return dragged.filter(field => {
            if (!responseKeys.has(field)) return true; // key not present at all
            const hasAnyValue = activeData.some(row => {
                const v = row[field];
                return v !== null && v !== undefined && v !== '';
            });
            return !hasAnyValue;
        });
    }, [activeData, config]);

    const getFiltersOtherData = (filter, currentFilterData = filterData) => {
        const otherData = {};
        const entity = headersData?.tableName?.[filter.key];
        if (!entity) return otherData;

        const DROPDOWN_TYPE =
            ["item", "product_catalogue_vw", "logged_actions", "item_master", "generic_item_master_report", "tna_dashboard_data"].includes(entity)
                ? "DYNAMIC_ITEM_CONFIG"
                : ["site", "asn_dashboard_details", "box_performance_details"].includes(entity)
                    ? "DYNAMIC_SITE_CONFIG"
                    : null;
        if (!DROPDOWN_TYPE) return otherData;

        const groupColumns = masterDbResponse?.[DROPDOWN_TYPE] || [];

        Object.keys(currentFilterData || {}).forEach(otherField => {
            if (otherField === filter.key) return; // skip the filter being queried
            const otherEntity = headersData?.tableName?.[otherField];
            if (!otherEntity || otherEntity !== entity) return; // different hierarchy

            const otherColumn = headersData?.tableColumnMap?.[otherField];
            if (!otherColumn || !groupColumns.includes(otherColumn)) return; // not in group

            const selected = Object.keys(currentFilterData?.[otherField] || {});
            if (selected.length > 0) otherData[otherColumn] = selected;
        });

        return otherData;
    }


    const getInputType = {
        "SEARCH": "dropdown",
        "TEXT": "text",
        "delivery_month": "date",
        "LOV": "dropdown",
    };

    const getInputProps = (filter) => {
        let type = filter?.type?.toLowerCase?.();
        switch (type) {
            case "search": {
                return {
                    viewDataType: "table-view",
                    modalMainClass: "use-as-generic-dropdown",
                    search: {
                        placeholder: `Search`,
                    },
                    headerKeys: {
                        "multiSelectCol": {},
                        [headersData?.tableColumnMap?.[filter.key]]: {
                            value: headersData?.[headersType]?.[filter.key] || "",
                        }
                    },
                    itemKeys: {
                        [headersData?.tableColumnMap?.[filter.key]]: [headersData?.tableColumnMap?.[filter.key]]
                    },
                    api: {
                        reduxState: "generalStore",
                        apiName: "getSearchFilters",
                        apiPayload: (params) => {
                            return {
                                "entity": headersData?.tableName?.[filter.key] || "",
                                "key": headersData?.tableColumnMap?.[filter.key] || "",
                                "code": filter?.code || "",
                                "search": params?.currentSearch || "",
                                "searchBy": params.searchFilter || "CONTAINS",
                                "pageNo": params?.currentPage || 1,
                                "pageSize": 20,
                                "moduleName": "",
                                "other_data": getFiltersOtherData(filter),
                                "columnName": {}
                            }
                        },
                        getMaxPage: (states, functions, response) => {
                            return Number(response?.resource?.maxPage || "");
                        },
                        apiExtraHeaders: {
                            "X-Scope-Id": "common.commonfunctionality.view"
                        }
                    },
                    getListData: (data) => data?.resource?.searchResultNew?.map(item => ({ ...item, [headersData?.tableColumnMap?.[filter.key]]: item?.[headersData?.tableColumnMap?.[filter.key]] })) || [],
                    multiselect: {
                        selectId: headersData?.tableColumnMap?.[filter.key]
                    }
                }
            }
            case "lov": {
                return {
                    viewDataType: "table-view",
                    modalMainClass: "use-as-generic-dropdown",
                    search: {
                        placeholder: `Search`,
                    },
                    headerKeys: {
                        "multiSelectCol": {},
                        [filter.key]: {
                            value: headersData?.[headersType]?.[filter.key] || "",
                        }
                    },
                    itemKeys: {
                        [filter.key]: [filter.key]
                    },
                    getListData: (data) => headersData?.["Lov Headers"]?.[filter.key]?.map?.(item => ({ [filter.key]: item })) || [],
                    multiselect: {
                        selectId: filter.key
                    }
                }
            }
        }
    }


    // ─── FILTER BAR ──────────────────────────────────────────────────────────────────

    const FilterBar = ({ data, filterFields, filters, onChange, fieldLabel }) => {
        if (filterFields.length === 0) return null;

        // Count how many filters are actually applied (have at least one selected value)
        const activeCount = filterFields.reduce(
            (n, f) => n + (filters?.[f]?.length > 0 ? 1 : 0),
            0
        );

        const clearAll = () => filterFields.forEach(f => onChange(f, []));

        console.log("filters fields", filterFields)


        return (
            <div className="mx-2 mt-2 mb-2 rounded border border-[#E2E5EE] bg-white">
                {/* <div className="flex items-center justify-between px-3 py-2 border-b border-[#EEF0F6] bg-[#FAFBFC] rounded-t-[6px]">
                    <div className="flex items-center gap-1.5">
                        {getIcon("Filters")}
                        <span className="uppercase text-[12px] tracking-[1px] font-semibold text-black">Filters</span>
                        {activeCount > 0 && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-[#EEF0FE] px-1.5 py-px text-[10px] font-semibold text-[#4338CA]">
                                {activeCount} active
                            </span>
                        )}
                    </div>
                    {activeCount > 0 && (
                        <GenericCustomButton type="underline" label="Clear all filters" onClick={clearAll} />
                    )}
                </div> */}

                {/* Filter pills */}
                <div className="flex flex-wrap items-center gap-2 px-3 py-2">
                    {filterFields.map(field => {
                        return (
                            <>
                                <GenericInput
                                    inputKey={field}
                                    label={headersData?.[headersType]?.[field]}
                                    type={getInputType?.[headersData?.columnType?.[field]] || "text"}
                                    inputProps={() => getInputProps({ type: "search", key: field })}
                                    iconRight={getInputType?.[headersData?.columnType?.[field]] == "dropdown" ? <Icons.DownArrowNew /> : null}
                                // value={}
                                />
                            </>
                        )
                    })}
                    {/* {filterFields.map(field => {
                        const uniqueVals = [...new Set(data.map(r => getVal(r, field)))].sort();
                        const selected = filters?.[field]?.length > 0;
                        return (
                            <div
                                key={field}
                                // ${selected ? 'bg-[#F5F6FF] border-[#C7D2FE]' : 'bg-white border-[#E2E5EE]'}
                                className={`flex flex-col transition-colors`}
                            >
                                <span className="text-[12px] tracking-[0.5px] font-semibold text-[#67768e] whitespace-nowrap mb-[5px]">
                                    {fieldLabel(field)}
                                </span>
                                <div className='bg-white border-[#dae4ee] rounded-[3px] border'>
                                    <Select
                                        mode="multiple"
                                        size="small"
                                        placeholder="All"
                                        value={filters[field] || []}
                                        onChange={vals => onChange(field, vals)}
                                        options={uniqueVals.map(v => ({ value: v, label: v }))}
                                        style={{ minWidth: 160, maxWidth: 280, height: 32 }}
                                        maxTagCount={1}
                                        allowClear
                                        bordered={false}
                                        popupMatchSelectWidth={false}
                                        className="pivot-filter-select"
                                    />
                                </div>
                            </div>
                        );
                    })} */}
                </div>
            </div>
        );
    }

    // ── Drag-drop / action handlers ────────────────────────────────────────────
    // All user-driven config mutators route through `setConfigDeferred` so React
    // treats the resulting pivot recompute as a non-urgent update. This means
    // drag/drop ghosts, filter input keystrokes and toggle clicks remain snappy
    // even on 10k-row pivots — the new table structure builds in the background.
    // Helper: when a field leaves the Filters area, drop its entry from the
    // `filters` object too. Otherwise stale selections persist and the request
    // payload would still carry ghost filter keys.
    const dropFilterValue = (filters, field) => {
        if (!filters || !(field in filters)) return filters;
        const { [field]: _drop, ...rest } = filters;
        return rest;
    };

    const handleDrop = useCallback((toArea, payload) => {
        const { field, fromArea, valueId } = payload;
        // Measure fields (po_quantity / po_count / po_amount) can only land in
        // Values; everything else can land anywhere except Values.
        if (isValuesOnlyField(field) && toArea !== 'values') {
            customToast({
                title: `${humanise(field)} can only be added to Values`,
                status: "error",
            });
            return;
        }
        if (!isValuesOnlyField(field) && toArea === 'values') {
            customToast({
                title: "Only PO Quantity, PO Count and PO Amount can be added to Values",
                status: "error",
            });
            return;
        }
        setConfigDeferred(prev => {
            if (toArea === 'filters') {
                const willBeInRows = prev.rowFields.includes(field) && fromArea !== 'rows';
                const willBeInCols = prev.colFields.includes(field) && fromArea !== 'columns';
                // if (!willBeInRows && !willBeInCols) {
                //     customToast({
                //         title: "Add field to Rows or Columns before filtering",
                //         status: "error",
                //     });
                //     return prev;
                // }
            }
            const next = { ...prev };
            if (fromArea === 'rows') next.rowFields = prev.rowFields.filter(f => f !== field);
            else if (fromArea === 'columns') next.colFields = prev.colFields.filter(f => f !== field);
            else if (fromArea === 'filters') {
                next.filterFields = prev.filterFields.filter(f => f !== field);
                next.filters = dropFilterValue(prev.filters, field);
            }
            else if (fromArea === 'values') next.valueFields = prev.valueFields.filter(vf => vf.id !== valueId);

            if (toArea === 'rows' && !next.rowFields.includes(field)) next.rowFields = [...next.rowFields, field];
            else if (toArea === 'columns' && !next.colFields.includes(field)) next.colFields = [...next.colFields, field];
            else if (toArea === 'filters' && !next.filterFields.includes(field)) next.filterFields = [...next.filterFields, field];
            else if (toArea === 'values') {
                const already = next.valueFields.find(vf => vf.field === field);
                if (!already) {
                    // if (next.valueFields.length >= 1) {
                    //     customToast({
                    //         title: "Only one field can be added to Values",
                    //         status: "error",
                    //     })
                    // } else {
                    const defaultAgg = numericFields.has(field) ? 'sum' : 'count';
                    next.valueFields = [...next.valueFields, { id: ++nextId, field, aggFunc: defaultAgg }];
                    // }
                }
            }
            return next;
        });
    }, [numericFields, setConfigDeferred]);

    const handleRemove = useCallback((area, field, valueId) => {
        setConfigDeferred(prev => {
            if (area === 'rows') return { ...prev, rowFields: prev.rowFields.filter(f => f !== field) };
            if (area === 'columns') return { ...prev, colFields: prev.colFields.filter(f => f !== field) };
            if (area === 'filters') return {
                ...prev,
                filterFields: prev.filterFields.filter(f => f !== field),
                filters: dropFilterValue(prev.filters, field),
            };
            if (area === 'values') return { ...prev, valueFields: prev.valueFields.filter(vf => vf.id !== valueId) };
            return prev;
        });
    }, [setConfigDeferred]);

    const handleAggChange = useCallback((valueId, aggFunc) => {
        setConfigDeferred(prev => ({
            ...prev,
            valueFields: prev.valueFields.map(vf => vf.id === valueId ? { ...vf, aggFunc } : vf),
        }));
    }, [setConfigDeferred]);

    const handleFilterChange = useCallback((field, vals) => {
        setConfigDeferred(prev => ({ ...prev, filters: { ...prev.filters, [field]: vals } }));
    }, [setConfigDeferred]);

    const handleFieldListDrop = useCallback((e) => {
        e.preventDefault();
        try {
            const payload = JSON.parse(e.dataTransfer.getData('text/plain'));
            const { field, fromArea, valueId } = payload;
            setConfigDeferred(prev => {
                if (fromArea === 'rows') return { ...prev, rowFields: prev.rowFields.filter(f => f !== field) };
                if (fromArea === 'columns') return { ...prev, colFields: prev.colFields.filter(f => f !== field) };
                if (fromArea === 'filters') return {
                    ...prev,
                    filterFields: prev.filterFields.filter(f => f !== field),
                    filters: dropFilterValue(prev.filters, field),
                };
                if (fromArea === 'values') return { ...prev, valueFields: prev.valueFields.filter(vf => vf.id !== valueId) };
                return prev;
            });
        } catch { }
    }, [setConfigDeferred]);

    const handleFieldDoubleClick = useCallback((field) => {
        // Double-click is a shortcut for "add to Values" — only valid for the
        // measure fields. Block silently for dimensions (no toast since the
        // user can still drag them to Rows/Cols/Filters).
        if (!isValuesOnlyField(field)) return;
        setConfigDeferred(prev => {
            const defaultAgg = numericFields.has(field) ? 'sum' : 'count';
            const alreadyValue = prev.valueFields.find(vf => vf.field === field);
            if (!alreadyValue) {
                // if (prev.valueFields.length >= 1) {
                //     customToast({
                //         title: "Only one field can be added to Values",
                //         status: "error",
                //     })
                //     return prev;
                // }
                return { ...prev, valueFields: [...prev.valueFields, { id: ++nextId, field, aggFunc: defaultAgg }] };
            }
            return prev;
        });
    }, [numericFields, setConfigDeferred]);

    const handleChange = (type, data) => {
        switch (type) {
            case "handleCountClick":
                setSelectedModal("viewCountData");
                setSelectedData(data);
                break;
            case "refreshReport": {
                dispatchHandler(dispatch, "getRefreshReportRequest", {
                    viewName: tnaDashRes?.mvName
                }, getPermissionKey("dashboard", "tna", "view"))
                break;
            }
            default: {
                const { val, innerType } = data || {};
                let nextVal;
                if (innerType == "SEARCH" || innerType == "LOV") {
                    // Search/Lov multiselect: val is an object keyed by selected
                    // label. Treat empty objects as "cleared" so the API filter
                    // and the visible pill reset together.
                    nextVal = val && typeof val === "object" && Object.keys(val).length > 0 ? val : null;
                } else if (innerType == "TEXT") {
                    nextVal = val?.target?.value || "";
                } else {
                    nextVal = val || null;
                }
                setFilterData(prev => ({ ...prev, [type]: nextVal }));
                break;
            }
        }
    }

    // Opens the ViewCountData drawer for a clicked po_count cell. Builds the
    // modal payload by layering the cell's row/col combo on top of the active
    // pivot filters. status_name (TNA stage) is the modal's primary filter and
    // gets pulled out as `statusName` for the API call.
    const onCountClick = useCallback((cell) => {
        const cellFilter = { ...requestFilter };
        const rowF = deferredTableStructure?.rowFields || [];
        const colF = deferredTableStructure?.colFields || [];
        (cell.rowCombo || []).forEach((v, i) => {
            if (rowF[i] && v !== undefined && v !== null && v !== '(All)') cellFilter[rowF[i]] = String(v);
        });
        (cell.colCombo || []).forEach((v, i) => {
            if (colF[i] && v !== undefined && v !== null) cellFilter[colF[i]] = String(v);
        });
        const statusName = cellFilter.status_name || cellFilter.statusName || "";
        delete cellFilter.status_name;
        setSelectedModal("viewCountData");
        setSelectedData({ filter: cellFilter, statusName, field: cell.field, ratio: cell.onTrackRatio });
    }, [requestFilter, deferredTableStructure]);

    console.log("filter Data", filterData)

    const getParentChildProps = () => {
        let payload = {
            closeModalV2: () => { setSelectedModal(false); setSelectedData("") },
            animationType: "bottom",
            parentModalOuterMostClassName: 'flex items-center justify-center',
        }
        switch (selectedModal) {
            case "viewCountData": return {
                ...payload,
                parentModalOuterMostClassName: 'flex items-center justify-center',
                postitionProps: { position: 'relative' },
                dimensions: { width: "80%", height: "90%", maxHeight: '850px', maxWidth: '1140px', borderRadius: 5 },
                animationType: "bottom",
                selectedData
            }

            case "designStage": return {
                ...payload,
                postitionProps: { top: 0, bottom: 0, left: "auto", right: 0, transform: "none", margin: 0 },
                dimensions: { width: "500px", height: "100vh", maxWidth: '100vw', borderRadius: 5 },
                animationType: "right",
                selectedData
            }

            default:
                break;
        }
    }

    const getChildComponent = () => {
        switch (selectedModal) {
            case "viewCountData": return ViewCountData;
            case "designStage": return DesignStagesModal;
        }
    }

    console.log("headers data ", headersData)

    // Count how many filterData entries are actually applied (non-empty value).
    // Object values are counted as active only when they have at least one key.
    const activeCount = Object.entries(filterData || {}).reduce((n, [, val]) => {
        if (val === null || val === undefined || val === "") return n;
        if (typeof val === "object" && Object.keys(val).length === 0) return n;
        return n + 1;
    }, 0);

    const appliedFilters = () => {
        const obj = {};
        Object.entries(filterData || {}).forEach(([key, val]) => {
            if (val === null || val === undefined || val === "") return;
            if (typeof val === "object" && Object.keys(val).length === 0) return;
            obj[key] = val;
        });

        return obj;
    }

    // Clear both filterData (drives the API call) and config.filters (legacy
    // client-side filter slot) so a single Clear All resets everything.
    const clearAll = () => {
        setFilterData({});
        config.filterFields.forEach(f => handleFilterChange(f, []));
    };

    console.log("filterData123123", filterData, appliedFilters())

    return (
        <div className="flex h-full gap-2">

            {showPanel && (
                <div className="w-[280px] shrink-0 flex flex-col gap-0 rounded bg-white border border-[#e2e8f0] shadow-[0 1px 2px rgba(15,23,42,0.04), 0 1px 3px rgba(15,23,42,0.04)] overflow-hidden">
                    {/* Field list header */}
                    <div className="px-4 pt-4 pb-3 border-b border-[#eef2f7]">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <svg viewBox="0 0 24 24" className="h-[16px] w-[16px] text-[#334155]" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 5h18M3 5l3 3M3 5l3-3M11 12h10M11 12l3 3M11 12l3-3M3 19h18M3 19l3 3M3 19l3-3"></path></svg>
                                <h3 className="text-[12px] font-bold tracking-wider text-[#334155] m-0">Fields Configuration</h3>
                            </div>
                            <span className="text-[11px] text-[#94a3b8]">{availableFields?.length} fields</span>
                        </div>

                        <div className="mt-[12px] relative">
                            <GenericInput
                                genInputExtraClass="!m-0"
                                placeholder="Search"
                                type="text" value={fieldSearch}
                                onChange={e => setFieldSearch(e?.target?.value || "")}
                                dependentFieldsObject={[fieldSearch]}
                            />
                        </div>
                    </div>

                    {/* Available fields */}
                    <ul
                        onDragOver={e => e.preventDefault()}
                        onDrop={handleFieldListDrop}
                        className="px-2 py-2 max-h-[260px] overflow-auto nice-scroll text-[13px]"
                    >
                        {availableFields.map(f => (
                            <li
                                key={f}
                                draggable
                                className="group flex items-center gap-2 rounded-lg px-2 py-1.5 cursor-grab hover:bg-[#f6f8fb]"
                                onDragStart={e => e.dataTransfer.setData('text/plain', JSON.stringify({ field: f, fromArea: 'list' }))}
                                onDoubleClick={() => handleFieldDoubleClick(f)}
                                title={f}
                            >
                                <span className={`h-1.5 w-1.5 rounded-full ${numericFields.has(f) ? 'bg-[#16a34a]' : 'bg-[#6c5ce7]'}`}></span>
                                <span className="flex-1 truncate text-ink-700">{fieldLabel(f)}</span>
                            </li>
                        ))}
                        {availableFields.length === 0 && (
                            <div className="text-[13px] text-[#000] text-center p-[8px]">
                                {allFieldKeys.length === 0 ? 'Loading fields…' : <NoDataFound className="!p-0" />}
                            </div>
                        )}
                    </ul>

                    {/* <div className="flex items-center justify-center gap-4 px-4 py-2 border-t border-slate-100 bg-slate-50/60 text-[11px]">
                        <span className="flex items-center gap-1.5"><span className="h-[8px] w-[8px] rounded-full bg-emerald-500"></span>numeric</span>
                        <span className="flex items-center gap-1.5"><span className="h-[8px] w-[8px] rounded-full bg-blue-500"></span>dimension</span>
                    </div> */}

                    <div className='flex-1 min-h-0 overflow-auto p-[12px] space-y-3 bg-slate-50/40 border-t border-slate-100'>
                        <DropZone title="Filters" area="filters" fields={config.filterFields} valueFields={config.valueFields} onDrop={handleDrop} onRemove={handleRemove} onAggChange={handleAggChange} fieldLabel={fieldLabel} />
                        <DropZone title="Columns" area="columns" fields={config.colFields} valueFields={config.valueFields} onDrop={handleDrop} onRemove={handleRemove} onAggChange={handleAggChange} fieldLabel={fieldLabel} />
                        <DropZone title="Rows" area="rows" fields={config.rowFields} valueFields={config.valueFields} onDrop={handleDrop} onRemove={handleRemove} onAggChange={handleAggChange} fieldLabel={fieldLabel} />
                        <DropZone title="Values" area="values" fields={[]} valueFields={config.valueFields} onDrop={handleDrop} onRemove={handleRemove} onAggChange={handleAggChange} fieldLabel={fieldLabel} />
                    </div>
                </div>
            )}

            {/* ── MAIN PIVOT AREA ── */}
            <div className="flex-1 flex flex-col min-w-0 rounded-[6px] bg-white border border-[#E2E5EE] shadow-[0_1px_2px_rgba(15,23,42,0.04)] overflow-hidden">

                {/* Header bar */}
                <div className="flex flex-wrap items-center gap-3 p-[10px] border-b border-[#EEF0F6] justify-between bg-white">
                    <div className="flex items-center gap-2.5">
                        <span className="flex h-[28px] w-[28px] items-center justify-center rounded-[6px] bg-[#EEF0FE] text-[#5d5fef]">
                            <svg className="h-[15px] w-[15px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                                <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                                <path d="M3 9h18M3 15h18M9 3v18M15 3v18"></path>
                            </svg>
                        </span>
                        <h2 className="text-[14px] font-semibold m-0 text-[#0F172A]">T&amp;A Dashboard Pivot Analysis</h2>
                        {/* Source-data badge: how many records came back from the backend after
                            applying filters. NOT the same as pivot row count — the pivot groups
                            these records into fewer aggregated rows. */}

                        {/* Pivot-output badge: how many rows the pivot actually renders, after
                            grouping & subtotals. Only shown when there's an active pivot. */}
                        {(deferredTableStructure && activeData.length != 0) && (
                            <span
                                className="inline-flex items-center gap-1 rounded-full bg-[#ECFDF5] px-2 py-0.5 text-[11px] font-semibold text-[#15803D]"
                                title="Number of rows in the rendered pivot (grouping + subtotals + grand total)."
                            >
                                <span className="h-1.5 w-1.5 rounded-full bg-[#10B981]"></span>
                                {deferredTableStructure.bodyRows.length.toLocaleString()} rows
                            </span>
                        )}
                        <span
                            className="inline-flex items-center gap-1 rounded-full bg-[#F1F5F9] px-2 py-0.5 text-[11px] font-semibold text-[#475569]"
                            title="Number of source records returned by the backend (after filters). The pivot groups these into the aggregated rows you see below."
                        >
                            <span className="h-1.5 w-1.5 rounded-full bg-[#5d5fef]"></span>
                            {/* {recordCount.toLocaleString()} records */}
                            {deferredTableStructure?.headerRows?.[0]?.length} columns
                        </span>
                        {isLoading && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-[#EEF0FE] text-[#5d5fef] px-2 py-0.5 text-[11px] font-medium">
                                <span className="h-1.5 w-1.5 rounded-full bg-[#5d5fef] animate-pulse"></span>
                                Loading…
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <div className='flex items-center gap-2'>
                            <label className='mb-0 text-xs font-semibold text-[#111D31]'>Fields Configuration</label>
                            <GenericToggleComponent
                                checked={showPanel}
                                onChange={() => setShowPanel(p => !p)}
                                checkedComponent="Show"
                                style={{ width: 64 }}
                                uncheckedComponent={<div className='text-right text-[#000]'>Hide</div>}
                            />
                        </div>
                        {/* <div className="h-5 w-px bg-[#E2E5EE]"></div> */}
                        <div id="tna-views-dropdown" className="relative">
                            {savedViews?.length > 0 && <button
                                type="button"
                                onClick={() => setShowViewsDropdown(s => !s)}
                                className="inline-flex items-center gap-2 rounded-[4px] border border-[#dde6ef] bg-white px-2.5 h-[30px] text-[13px] font-medium text-[#23235f] hover:bg-[#f5f5f5] transition-colors w-[180px]"
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M10 20a1 1 0 0 0 .553.895l2 1A1 1 0 0 0 14 21v-7a2 2 0 0 1 .517-1.341L21.74 4.67A1 1 0 0 0 21 3H3a1 1 0 0 0-.742 1.67l7.225 7.989A2 2 0 0 1 10 14z" />
                                </svg>
                                <TextWithTooltip as="span" width="100%" className={`max-w-[100px] truncate text-left ${activeViewName ? "min-w-[100px]" : ""}`} text={activeViewName || 'Saved Views'} />
                                {activeViewName && (
                                    <span
                                        role="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            resetToInitialState();
                                        }}
                                        className="text-[#94A3B8] hover:text-[#0F172A]"
                                        title="Clear active view and reset to initial state"
                                    >
                                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                                            <path d="M1 1 L9 9 M9 1 L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                        </svg>
                                    </span>
                                )}
                                <svg className={activeViewName ? '' : 'ml-[30px]'} width="10" height="10" viewBox="0 0 12 12" fill="none">
                                    <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>}
                            {showViewsDropdown && (
                                <div className="absolute right-0 top-full mt-1 z-30 w-[260px] rounded-[3px] bg-white shadow-[0_2.71px_18.99px_hsla(0,0%,63%,.26)]">
                                    <div className="px-3 py-2 border-b border-[#EEF0F6] text-[11px] font-semibold tracking-wider text-[#23235f]">
                                        SAVED VIEWS
                                    </div>
                                    <ul className="max-h-[260px] min-h-[120px] overflow-auto py-1">
                                        {savedViews.length === 0 && (
                                            <li className="px-3 py-3 text-[13px] text-[#94A3B8] text-center">
                                                No saved views yet
                                            </li>
                                        )}
                                        {savedViews?.map?.((view) => {
                                            const isActive = activeViewName === view.filterName;
                                            return (
                                                <li
                                                    key={view.filterName}
                                                    className={`group flex items-center justify-between gap-2 px-3 py-1.5 cursor-pointer text-[12px] font-medium capitalize border-l-[2px] ${isActive ? ' text-[#23235f] border-l-primaryColor bg-primary_w_less_opacity' : 'border-l-[#fff] text-[#23235f] hover:bg-primary_w_less_opacity hover:border-l-primaryColor'}`}
                                                    onClick={() => {
                                                        applyView(view);
                                                        setShowViewsDropdown(false);
                                                    }}
                                                    title={view.filterName}
                                                >
                                                    <span className="flex items-center gap-1.5 min-w-0">
                                                        {/* {view.is_default === 1 && (
                                                            <span className={`text-[12px] ${isActive ? 'text-white' : 'text-[#F59E0B]'}`}>★</span>
                                                        )} */}
                                                        <span className="truncate">{view.filterName}</span>
                                                    </span>
                                                    <button
                                                        type="button"
                                                        className={`relative shrink-0 inline-flex items-center justify-center w-[26px] h-[26px] rounded-full -mr-1 ${isActive ? 'hover:bg-white/20' : 'hover:bg-[#e2e8f0]'}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setViewMenuOpenFor(viewMenuOpenFor === view.filterName ? null : view.filterName);
                                                        }}
                                                        aria-label="View options"
                                                    >
                                                        <svg width="4" height="16" viewBox="0 0 4 16" fill="none">
                                                            <circle cx="2" cy="2.5" r="1.6" fill="currentColor" />
                                                            <circle cx="2" cy="8" r="1.6" fill="currentColor" />
                                                            <circle cx="2" cy="13.5" r="1.6" fill="currentColor" />
                                                        </svg>
                                                        {viewMenuOpenFor === view.filterName && (
                                                            <ul
                                                                className="absolute right-0 top-full mt-1 z-40 min-w-[150px] rounded-[3px] bg-white py-1 shadow-[0_4px_14px_-3px_hsla(0,0%,55%,.25)] text-xs font-medium text-[#21314b]"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <li
                                                                    className="px-2.5 py-1 hover:bg-[#f5f5f5] cursor-pointer capitalize text-left"
                                                                    onClick={() => { handleMarkDefaultView(view); setViewMenuOpenFor(null); }}
                                                                >
                                                                    {view.is_default === 1 ? 'Remove as Default' : 'Mark as Default'}
                                                                </li>
                                                                <li
                                                                    className="px-2.5 py-1 text-red-600 hover:bg-[#f5f5f5] cursor-pointer capitalize text-left"
                                                                    onClick={() => { handleDeleteView(view); setViewMenuOpenFor(null); }}
                                                                >
                                                                    Delete View
                                                                </li>
                                                            </ul>
                                                        )}
                                                    </button>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            )}
                        </div>
                        <GenericCustomButton
                            className='!h-[30px]'
                            type="primary"
                            // size="small"
                            label="Save View"
                            onClick={openSaveModal}
                        />
                    </div>
                </div>

                {(tnaDashRes && tnaDashRes?.mvName) && <div className='p-[10px] w-full flex items-center justify-end gap-3 border-b border-[#EEF0F6]'>
                    {tnaDashRes?.mvUpdateDate && <div className='w-auto flex items-center gap-1.5'>
                        <HistoryIcon size={14} color='#67768E' className='shrink-0' />
                        <p className='mb-0 text-[11px] font-normal text-[#525676]'>Last Updated:</p>
                        <p className='mb-0 text-[11px] font-normal text-[#67768E]'>{dayjs(tnaDashRes?.mvUpdateDate || "").format("DD MMM YYYY, h:mm:ss a")}</p>
                    </div>}
                    {/* {tnaDashRes && tnaDashRes?.mvName && ( */}
                        <GenericCustomButton
                            label="Refresh Data"
                            type="primary"
                            loading={getRefreshReportData?.isLoading}
                            onClick={() => handleChange("refreshReport")}
                        />
                    {/* )} */}
                </div>}

                {config.filterFields?.length ? <div className={`mx-2 mt-2 mb-2 rounded border border-[#E2E5EE] bg-white relative z-[10]`}>
                    <div className={`flex items-center justify-between px-3 py-2 bg-[#FAFBFC] hover:brightness-95 cursor-pointer ${showFilters ? 'rounded-t-[3px] border-b border-[#EEF0F6]' : 'rounded-[3px]'}`} onClick={() => setShowFilters(prev => !prev)}>
                        <div className="flex items-center gap-1.5">
                            {getIcon("Filters")}
                            <span className="text-[12px] tracking-[1px] font-semibold text-black">Filters</span>
                            {activeCount > 0 && (
                                <>
                                    <span className="inline-flex items-center gap-1 rounded-full bg-[#EEF0FE] px-1.5 py-px text-[10px] font-semibold text-[#4338CA]">
                                        {activeCount} active
                                    </span>
                                    {/* <Popover
                                        key={'appliedFiltersPopover'}
                                        trigger="click"
                                        open={showFiltersPopover}
                                        onOpenChange={(open) =>
                                            setShowFiltersPopover(open ? true : null)
                                        }
                                        placement="bottom"
                                        styles={{
                                            body: {
                                                padding: 0,
                                            },
                                        }}
                                        overlayInnerStyle={{
                                            fontFamily: 'Poppins'
                                        }}
                                        content={
                                            <div className='applied-filters-popup max-w-[500px] leading-[12px] text-[11px] overflow-y-auto bg-white min-w-[400px] appliedFilterModalOpen'>
                                                <div className='afp-head px-4 pt-[1rem]'>
                                                    <LanguageTranslator tag='h3' className='font-bold mb-3 text-sm leading-[12px]'>Applied Filters</LanguageTranslator>
                                                    <LanguageTranslator tag='p' className='text-[#525676] italic'>Below are the filters applied</LanguageTranslator>
                                                </div>
                                                <div className="afp-body max-h-[30vh] overflow-y-auto px-4 pb-2" style={{
                                                    maxHeight: 200
                                                }}>
                                                    {Object.entries(appliedFilters()).map(([filterKey, values]) => (
                                                        <div className="filterDet mt-[1rem] mb-2">
                                                            <LanguageTranslator tag='label' className='filterLabel'>{headersData?.[headersType]?.[filterKey]}</LanguageTranslator>
                                                            <div className='filterData flex items-center flex-wrap gap-2'>
                                                                {Object.keys(values).map(item =>(
                                                                    <span className='afpbr-value'>{item}</span>
                                                                ))}
                                                                <span className='clear-filter bg-[#f6f6f6] rounded-[50%] cursor-pointer ml-[3px] p-[3px] hover:bg-primary' onClick={()=> clearSingleFilter(newKey)}><Icons.ModalCloseIcon /></span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        }
                                    >
                                        <div
                                            className={'relative flex items-center gap-1.5 h-[26px] text-[11px] font-medium bg-primary_w_less_opacity rounded-[3px] text-fontBlack px-2'}
                                            onClick={e => e.stopPropagation()}
                                        >
                                            <div className='relative'><div><Icons.GCFilterIcon height={17} width={17}/></div><div className='absolute top-[3px] right-[0px] p-[1px] bg-primary rounded-full'><Check color='white' strokeWidth={5} size={6} /></div></div>
                                            <span>Filtered</span>
                                        </div>
                                    </Popover> */}
                                </>
                            )}
                        </div>
                        <div className='flex items-center gap-3'>
                            {activeCount > 0 && <>
                                    <GenericCustomButton type="underline" label="Clear all filters" onClick={clearAll} />
                                    <div className='h-[12px] w-[1px] shrink-0 bg-borderSlateGrey'></div>
                                </>
                            }
                            <div className={`h-[22px] aspect-square p-[2px] rounded-full flex items-center justify-center bg-[#e7e7e7] duration-200 transition-transform hover:opacity-75 ${showFilters ? 'rotate-180' : 'rotate-0'}`}><ChevronDown size={16}/></div>
                        </div>
                    </div>

                    {/* Filter */}
                    <div className={`duration-200 ${showFilters ? 'visible opacity-100 h-auto translate-y-0 pointer-events-auto' : 'invisible opacity-0 h-[0] translate-y-2 pointer-events-none overflow-hidden'}`}>
                        <div className={`px-3 py-2 flex flex-wrap items-center gap-2`}>
                            {config.filterFields.map(field => {
                                const raw = filterData?.[field];
                                const displayValue =
                                    raw && typeof raw === "object"
                                        ? Object.keys(raw).join(", ")
                                        : (raw || "");
                                return (
                                    <GenericInput
                                        genInputExtraClass="!w-[24%]"
                                        key={field}
                                        inputKey={field}
                                        label={headersData?.[headersType]?.[field]}
                                        type={getInputType?.[headersData?.columnType?.[field]] || "text"}
                                        inputProps={() => getInputProps({ type: headersData?.columnType?.[field], key: field })}
                                        iconRight={getInputType?.[headersData?.columnType?.[field]] == "dropdown" ? <Icons.DownArrowNew /> : null}
                                        value={displayValue}
                                        actualInputValue={raw}
                                        onChange={(data) => handleChange(field, { val: data, innerType: headersData?.columnType?.[field] })}
                                        dependentFieldsObject={[raw]}
                                    />
                                )
                            })}
                        </div>
                    </div>
                </div> : null}
                
                {isLoading ? <div className='h-full flex flex-col'><div className='generic-skeleton !mb-0 !h-full w-full'></div></div> : allFieldKeys.length > 0 && (
                    activeData.length === 0 ||
                    (deferredTableStructure && (pivotData?.filtered?.length ?? 0) === 0)
                ) ? (
                    <div className="flex items-center justify-center flex-1">
                        <NoDataFound />
                    </div>
                ) : deferredTableStructure ? (
                    <div className={`flex-1 min-h-0 flex flex-col ${isStaleRender ? 'opacity-70 ' : ''}transition-opacity`}>
                        <PivotCellContext.Provider value={{ onCountClick }}>
                            <PivotGrid
                                headerRows={deferredTableStructure.headerRows}
                                bodyRows={deferredTableStructure.bodyRows}
                                onSortValue={handleSortValue}
                                valueSort={valueSort}
                            />
                        </PivotCellContext.Provider>
                    </div>
                ) : (() => {
                    // Context-aware empty state — same rules as Excel:
                    // a pivot needs at least one Row and one Value to produce data.
                    let title, hint;
                    if (allFieldKeys.length === 0) {
                        title = isLoading ? 'Fetching headers & data…' : 'No data available';
                        hint = isLoading ? 'Hang tight, this only takes a moment.' : 'Headers haven\'t loaded — check the network response.';
                    } else if (config.rowFields.length === 0 && config.valueFields.length === 0) {
                        title = 'Build your pivot table';
                        hint = 'Drag a field to Rows and a measure to Values to get started.';
                    } else if (config.valueFields.length === 0) {
                        title = 'Add a field to Values';
                        hint = 'A pivot needs at least one measure (Count / Sum / Avg…) to display data.';
                    } else if (config.rowFields.length === 0) {
                        title = 'Add a field to Rows';
                        hint = 'Drag a field to Rows to group your data by.';
                    } else {
                        title = 'Configure your pivot';
                        hint = 'Add fields to Rows and Values to build a pivot table.';
                    }
                    return (
                        <div className="flex items-center justify-center h-[256px]">
                            <div className="text-center max-w-[360px]">
                                <TableOutlined className="text-[48px] mb-4 opacity-30 text-slate-400" />
                                <p className='text-[13px] text-slate-600 font-medium'>{title}</p>
                                <p className='text-[11px] text-slate-400 mt-1'>{hint}</p>
                            </div>
                        </div>
                    );
                })()}
            </div>
            {selectedModal && <ParentModal
                getParentChildProps={getParentChildProps()}
                childComponent={getChildComponent()}
                closeModal={() => { setSelectedModal(false); setSelectedData("") }}
            />}

            {showSaveViewModal && (
                <div
                    className="fixed inset-0 z-[999] flex items-center justify-center bg-black/40"
                    onClick={() => setShowSaveViewModal(false)}
                >
                    <div
                        className="w-[650px] rounded bg-white shadow-xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="px-[20px] pt-[15px] pb-[15px] border-b border-[#c1d8f070]">
                            <div className="flex items-center justify-between">
                                <h3 className="m-0 text-base font-bold text-[#12203c]">Save View</h3>
                                <button
                                    className="border-2 border-black rounded-full p-[5px] text-black hover:bg-[#F1F5F9]"
                                    onClick={() => setShowSaveViewModal(false)}
                                    aria-label="Close"
                                >
                                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                                        <path d="M1 1 L9 9 M9 1 L1 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                    </svg>
                                </button>
                            </div>
                            <p className="mb-0 text-xs font-medium text-[#97abbf]">
                                Create a new view or replace an existing one
                            </p>
                        </div>

                        <div className="px-[20px] py-[15px]">
                            <div className="grid grid-cols-2 gap-5">
                                <div>
                                    <label className="flex items-center gap-2 text-xs font-medium text-[#12203c] cursor-pointer select-none">
                                        <span className="relative inline-flex items-center justify-center w-[16px] h-[16px]">
                                            <input
                                                type="radio"
                                                name="saveViewMode"
                                                checked={saveModalMode === "new"}
                                                onChange={() => { setSaveModalMode("new"); setReplaceTargetName(""); }}
                                                className="peer absolute opacity-0 w-full h-full cursor-pointer"
                                            />
                                            <span className="w-[16px] h-[16px] rounded-full border-2 border-[#c1d8f0] peer-checked:border-primaryColor flex items-center justify-center transition-colors">
                                                <span className={`w-[8px] h-[8px] rounded-full bg-primaryColor ${saveModalMode === "new" ? "scale-100" : "scale-0"} transition-transform`}></span>
                                            </span>
                                        </span>
                                        Save as new view
                                    </label>
                                    <input
                                        type="text"
                                        autoFocus={saveModalMode === "new"}
                                        disabled={saveModalMode !== "new"}
                                        value={viewNameInput}
                                        onChange={(e) => setViewNameInput(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleSaveView(); }}
                                        placeholder="Enter view name"
                                        className="mt-3 w-full border border-[#c1d8f0] rounded px-3 py-2 text-[13px] text-[#12203c] outline-none focus:border-primaryColor disabled:bg-[#F8FAFC] disabled:text-[#94A3B8]"
                                    />
                                </div>
                                <div>
                                    <label className={`flex items-center gap-2 text-xs font-medium text-[#12203c] select-none ${savedViews.length === 0 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                                        <span className="relative inline-flex items-center justify-center w-[16px] h-[16px]">
                                            <input
                                                type="radio"
                                                name="saveViewMode"
                                                checked={saveModalMode === "replace"}
                                                disabled={savedViews.length === 0}
                                                onChange={() => { setSaveModalMode("replace"); setViewNameInput(""); }}
                                                className="peer absolute opacity-0 w-full h-full cursor-pointer disabled:cursor-not-allowed"
                                            />
                                            <span className="w-[16px] h-[16px] rounded-full border-2 border-[#c1d8f0] peer-checked:border-primaryColor flex items-center justify-center transition-colors">
                                                <span className={`w-[8px] h-[8px] rounded-full bg-primaryColor ${saveModalMode === "replace" ? "scale-100" : "scale-0"} transition-transform`}></span>
                                            </span>
                                        </span>
                                        Replace with existing view
                                    </label>
                                    <select
                                        disabled={saveModalMode !== "replace" || savedViews.length === 0}
                                        value={replaceTargetName}
                                        onChange={(e) => setReplaceTargetName(e.target.value)}
                                        className="mt-3 w-full border border-[#c1d8f0] rounded px-3 py-2 text-[13px] text-[#12203c] outline-none focus:border-primaryColor disabled:bg-[#F8FAFC] disabled:text-[#94A3B8] bg-white"
                                    >
                                        <option value="">Select view</option>
                                        {savedViews.map(v => (
                                            <option key={v.filterName} value={v.filterName}>{v.filterName}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="mt-5">
                                <h4 className="text-sm font-semibold text-[#12203c] mb-2.5">Selected Configuration</h4>
                                <div className="border border-dashed border-[#c1d8f0] rounded-[7px] pt-2.5 px-3 pb-0 min-h-[64px] flex flex-wrap content-start">
                                    {config.rowFields.map(f => (
                                        <div key={`r-${f}`} className="inline-flex items-center py-[7px] px-[15px] bg-[#f2f5f8] rounded-[24px] mr-[13px] mb-2.5">
                                            <span className="text-xs font-medium text-[#5677b1] pr-2">Row</span>
                                            <span className="text-xs font-semibold text-[#5677b1] pl-2 border-l border-[#c1d8f0]">{fieldLabel(f)}</span>
                                        </div>
                                    ))}
                                    {config.colFields.map(f => (
                                        <div key={`c-${f}`} className="inline-flex items-center py-[7px] px-[15px] bg-[#f2f5f8] rounded-[24px] mr-[13px] mb-2.5">
                                            <span className="text-xs font-medium text-[#5677b1] pr-2">Column</span>
                                            <span className="text-xs font-semibold text-[#5677b1] pl-2 border-l border-[#c1d8f0]">{fieldLabel(f)}</span>
                                        </div>
                                    ))}
                                    {config.valueFields.map(vf => (
                                        <div key={`v-${vf.id}`} className="inline-flex items-center py-[7px] px-[15px] bg-[#f2f5f8] rounded-[24px] mr-[13px] mb-2.5">
                                            <span className="text-xs font-medium text-[#5677b1] pr-2">{AGGREGATIONS[vf.aggFunc]?.label || vf.aggFunc}</span>
                                            <span className="text-xs font-semibold text-[#5677b1] pl-2 border-l border-[#c1d8f0]">{fieldLabel(vf.field)}</span>
                                        </div>
                                    ))}
                                    {config.filterFields.map(f => (
                                        <div key={`f-${f}`} className="inline-flex items-center py-[7px] px-[15px] bg-[#f2f5f8] rounded-[24px] mr-[13px] mb-2.5">
                                            <span className="text-xs font-medium text-[#5677b1] pr-2">Filter</span>
                                            <span className="text-xs font-semibold text-[#5677b1] pl-2 border-l border-[#c1d8f0]">{fieldLabel(f)}</span>
                                        </div>
                                    ))}
                                    {config.rowFields.length === 0 && config.colFields.length === 0 && config.valueFields.length === 0 && config.filterFields.length === 0 && (
                                        <p className="m-0 text-xs text-[#97abbf] py-2">No configuration to save yet — add rows, columns or values first.</p>
                                    )}
                                </div>
                            </div>

                            {saveModalMode === "replace" && replaceTargetName && (
                                <div className="mt-4 flex items-start gap-2 rounded border border-[#FDE68A] bg-[#FFFBEB] px-3 py-2">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="mt-0.5 shrink-0">
                                        <path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" stroke="#D97706" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <p className="m-0 text-xs font-medium text-[#92400E]">
                                        On saving, the existing view <span className="font-semibold">"{replaceTargetName}"</span> will be replaced with the current pivot configuration.
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="px-10 py-4 border-t border-[#c1d8f070] flex justify-center">
                            <GenericCustomButton
                                type="primary"
                                size="large"
                                label={saveModalMode === "replace" ? "Replace View" : "Save View"}
                                className="!h-[36px] !min-w-[120px]"
                                disabled={
                                    createQuickFiltersData?.isLoading ||
                                    (saveModalMode === "new" && !viewNameInput.trim()) ||
                                    (saveModalMode === "replace" && !replaceTargetName)
                                }
                                onClick={handleSaveView}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
